import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, useToast } from '../contexts/AppContexts';
import { useFieldLock } from '../hooks/useFieldLock';
import { AlertTriangle, ArrowLeft, Building, Camera, ChevronDown, ChevronRight, Edit, Map, Plus, Printer, Save, Shield, ShieldAlert, Trash2, Upload, Users } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';
import useImageDrop from '../utils/useImageDrop';
import NestedObjectives from './NestedObjectives';

const API = `${API_BASE_URL}/api`;

const ExerciseBuilderWizard = ({ onBack, editingExercise = null, initialStep = null, returnTo = null, LeafletMapping = null, MSELView = null }) => {
  const toast = useToast();
  const { token } = useAuth(); // Get token from auth context
  const { getFieldProps } = useFieldLock('exercise-builder');
  const [currentStep, setCurrentStep] = useState(initialStep || 1);
  const [exerciseData, setExerciseData] = useState({
    // Step 1: Exercise
    exercise_image: null,
    exercise_name: '',
    exercise_type: '',
    exercise_description: '',
    location: '',
    latitude: '',
    longitude: '',
    zoom_level: 8,
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    // Step 2: Scope
    scope_description: '',
    scope_hazards: '',
    scope_geographic_area: '',
    scope_functions: '',
    scope_organizations: '',
    scope_personnel: '',
    scope_exercise_type: '',
    // Step 3: Purpose
    purpose_description: '',
    // Step 4: Scenario
    scenario_image: null,
    scenario_name: '',
    scenario_description: '',
    scenario_latitude: 0,
    scenario_longitude: 0
  });
  const [imagePreview, setImagePreview] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Auto-save state — silently persists the draft every 60s while editing
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);

  // Validation error states
  const [exerciseValidationErrors, setExerciseValidationErrors] = useState({
    latitude: '',
    longitude: ''
  });
  const [scenarioValidationErrors, setScenarioValidationErrors] = useState({
    latitude: '',
    longitude: ''
  });
  const [eventValidationErrors, setEventValidationErrors] = useState({
    latitude: '',
    longitude: ''
  });

  // Validation functions for latitude and longitude
  const validateLatitude = (value) => {
    if (value === '') return ''; // Allow empty for optional fields
    
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Latitude must be a valid decimal number';
    }
    
    if (num < -90 || num > 90) {
      return 'Latitude must be between -90.0000 and 90.0000';
    }
    
    // Check decimal format (should have at least 4 decimal places)
    const decimalParts = value.toString().split('.');
    if (decimalParts.length === 1 || decimalParts[1].length < 4) {
      return 'Latitude should be in decimal format with 4 decimal places (e.g., 45.0000)';
    }
    
    return '';
  };

  const validateLongitude = (value) => {
    if (value === '') return ''; // Allow empty for optional fields
    
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Longitude must be a valid decimal number';
    }
    
    if (num < -180 || num > 180) {
      return 'Longitude must be between -180.0000 and 180.0000';
    }
    
    // Check decimal format (should have at least 4 decimal places)
    const decimalParts = value.toString().split('.');
    if (decimalParts.length === 1 || decimalParts[1].length < 4) {
      return 'Longitude should be in decimal format with 4 decimal places (e.g., 97.0000)';
    }
    
    return '';
  };

  const handleLatitudeChange = (value, type = 'exercise') => {
    const error = validateLatitude(value);
    
    if (type === 'exercise') {
      setExerciseData(prev => ({ ...prev, latitude: value }));
      setExerciseValidationErrors(prev => ({ ...prev, latitude: error }));
    } else if (type === 'scenario') {
      setExerciseData(prev => ({ ...prev, scenario_latitude: value }));
      setScenarioValidationErrors(prev => ({ ...prev, latitude: error }));
    }
  };

  const handleLongitudeChange = (value, type = 'exercise') => {
    const error = validateLongitude(value);
    
    if (type === 'exercise') {
      setExerciseData(prev => ({ ...prev, longitude: value }));
      setExerciseValidationErrors(prev => ({ ...prev, longitude: error }));
    } else if (type === 'scenario') {
      setExerciseData(prev => ({ ...prev, scenario_longitude: value }));
      setScenarioValidationErrors(prev => ({ ...prev, longitude: error }));
    }
  };
  const [organizationValidationErrors, setOrganizationValidationErrors] = useState({
    latitude: '',
    longitude: ''
  });

  // Dynamic lists for add functionality
  const [goals, setGoals] = useState([]);
  const [objectives, setObjectives] = useState([]);
  // Which goal cards are collapsed (id-set). Missing id = expanded (default).
  const [collapsedGoals, setCollapsedGoals] = useState(new Set());
  const toggleGoalCollapsed = (id) => {
    setCollapsedGoals(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Safety concern cards — same collapse pattern as goals.
  const [collapsedSafety, setCollapsedSafety] = useState(new Set());
  const toggleSafetyCollapsed = (id) => {
    setCollapsedSafety(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Standard of Conduct cards — same collapse pattern.
  const [collapsedStandards, setCollapsedStandards] = useState(new Set());
  const toggleStandardCollapsed = (id) => {
    setCollapsedStandards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Generic collapse pattern for the remaining flat-list steps (Step 6/9/10/11/12/13/14)
  const _makeToggler = (setter) => (id) => setter(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [collapsedFunctions, setCollapsedFunctions] = useState(new Set());
  const toggleFunctionCollapsed = _makeToggler(setCollapsedFunctions);
  const [collapsedSites, setCollapsedSites] = useState(new Set());
  const toggleSiteCollapsed = _makeToggler(setCollapsedSites);
  const [collapsedCoordinators, setCollapsedCoordinators] = useState(new Set());
  const toggleCoordinatorCollapsed = _makeToggler(setCollapsedCoordinators);
  const [collapsedCodeWords, setCollapsedCodeWords] = useState(new Set());
  const toggleCodeWordCollapsed = _makeToggler(setCollapsedCodeWords);
  const [collapsedCallsigns, setCollapsedCallsigns] = useState(new Set());
  const toggleCallsignCollapsed = _makeToggler(setCollapsedCallsigns);
  const [collapsedAssumptions, setCollapsedAssumptions] = useState(new Set());
  const toggleAssumptionCollapsed = _makeToggler(setCollapsedAssumptions);
  const [collapsedArtificialities, setCollapsedArtificialities] = useState(new Set());
  const toggleArtificialityCollapsed = _makeToggler(setCollapsedArtificialities);
  const [events, setEvents] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [expandedOrgIds, setExpandedOrgIds] = useState(new Set());
  const toggleOrgExpanded = (id) => {
    setExpandedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Per-organization "add person" form state
  const emptyPerson = { first_name: '', last_name: '', physical_location: '', phone: '', phone_extension: '', email: '', title: '', facility: '' };
  const [personForms, setPersonForms] = useState({}); // { [orgId]: person }
  const [editingPersonKey, setEditingPersonKey] = useState(null); // "orgId:personId" when editing
  const getPersonForm = (orgId) => personForms[orgId] || emptyPerson;
  const setPersonForm = (orgId, patch) => {
    setPersonForms((prev) => ({ ...prev, [orgId]: { ...(prev[orgId] || emptyPerson), ...patch } }));
  };
  const resetPersonForm = (orgId) => {
    setPersonForms((prev) => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
  };
  const savePersonToOrg = (orgId) => {
    const form = getPersonForm(orgId);
    if (!form.first_name?.trim() || !form.last_name?.trim()) return;
    const editingId = editingPersonKey?.startsWith(`${orgId}:`) ? editingPersonKey.split(':')[1] : null;
    const nextOrgs = organizations.map((org) => {
      if (org.id !== orgId) return org;
      const persons = Array.isArray(org.persons) ? org.persons : [];
      if (editingId) {
        return { ...org, persons: persons.map((p) => (String(p.id) === String(editingId) ? { ...form, id: p.id } : p)) };
      }
      return { ...org, persons: [...persons, { ...form, id: Date.now() }] };
    });
    setOrganizations(nextOrgs);
    persistOrganizations(nextOrgs);
    setEditingPersonKey(null);
    resetPersonForm(orgId);
  };
  const editPerson = (orgId, person) => {
    setPersonForms((prev) => ({ ...prev, [orgId]: { ...emptyPerson, ...person } }));
    setEditingPersonKey(`${orgId}:${person.id}`);
    setExpandedOrgIds((prev) => new Set(prev).add(orgId));
  };
  const removePerson = (orgId, personId) => {
    const nextOrgs = organizations.map((org) => (
      org.id === orgId
        ? { ...org, persons: (org.persons || []).filter((p) => String(p.id) !== String(personId)) }
        : org
    ));
    setOrganizations(nextOrgs);
    persistOrganizations(nextOrgs);
    if (editingPersonKey === `${orgId}:${personId}`) {
      setEditingPersonKey(null);
      resetPersonForm(orgId);
    }
  };
  const TITLE_OPTIONS = [
    'Commanding Officer', 'Deputy Commander', 'Director', 'Employee', 'Mayor',
    'Counsellor', 'Decision Maker', 'Influencer', 'Staff member', 'Specialist',
    'First Responder', 'Crew member', 'None', 'Other',
  ];
  const FACILITY_OPTIONS = [
    'Police Station', 'Fire Hall', 'EMS Station', 'Hospital', 'School',
    'Sports Complex', 'Community Hall', 'City Hall', 'Evacuation Center',
    'Operations Center', 'Command Post', 'Mobile Unit', 'Other',
  ];
  const [exerciseSites, setExerciseSites] = useState([]); // Step 10 — image + proposed location + exercise function + participant guidance
  const [currentSite, setCurrentSite] = useState({ proposed_location: '', exercise_function: '', participant_guidance: '', image: null });
  const [codeWords, setCodeWords] = useState([]);
  const [callsigns, setCallsigns] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [assumptions, setAssumptions] = useState([]);
  const [artificialities, setArtificialities] = useState([]);
  const [safetyConcerns, setSafetyConcerns] = useState([]);
  const [standardsOfConduct, setStandardsOfConduct] = useState([]);
  const [currentStandardOfConduct, setCurrentStandardOfConduct] = useState({ name: '', conduct_expected: '' });
  const [editingStandardOfConductId, setEditingStandardOfConductId] = useState(null);
  
  // Safety Officer state (auto-populated from participants)
  const [safetyOfficer, setSafetyOfficer] = useState({
    firstName: '',
    lastName: '',
    cellPhone: '',
    participantId: null
  });
  const [safetyOfficerLoading, setSafetyOfficerLoading] = useState(false);

  // Current form states for adding items
  const [currentGoal, setCurrentGoal] = useState({ name: '', broad_statement: '', description: '' });
  const [editingGoalId, setEditingGoalId] = useState(null); // Track which goal is being edited
  const [currentObjective, setCurrentObjective] = useState({ name: '', description: '' });
  const [editingObjectiveId, setEditingObjectiveId] = useState(null); // Track which objective is being edited
  const [currentEvent, setCurrentEvent] = useState({ 
    name: '', description: '', actions: '', latitude: 0, longitude: 0, 
    start_date: '', start_time: '', end_date: '', end_time: '', 
    tier_scale: '', escalation_value: 'none' 
  });
  const [currentFunction, setCurrentFunction] = useState({ name: '', description: '' });
  const [editingFunctionId, setEditingFunctionId] = useState(null); // Track which function is being edited
  const [currentOrganization, setCurrentOrganization] = useState({ 
    name: '', description: '', home_base: '', contact_first_name: '', 
    contact_last_name: '', contact_phone: '', contact_email: '',
    latitude: '', longitude: '', organization_image: null
  });
  const [editingOrganizationId, setEditingOrganizationId] = useState(null); // Track which organization is being edited
  const [organizationImagePreview, setOrganizationImagePreview] = useState(null); // Preview for organization image
  const [currentCodeWord, setCurrentCodeWord] = useState({ word: '', meaning: '' });
  const [editingCodeWordId, setEditingCodeWordId] = useState(null); // Track which code word is being edited
  const [currentCallsign, setCurrentCallsign] = useState({ callsign: '', description: '' });
  const [editingCallsignId, setEditingCallsignId] = useState(null); // Track which callsign is being edited
  const [currentFrequency, setCurrentFrequency] = useState({ 
    frequency: '', type: '', description: '', primary_backup: 'Primary', modulation_type: '' 
  });
  const [editingFrequencyId, setEditingFrequencyId] = useState(null); // Track which frequency is being edited
  // ICS 205 Communication Plan state moved to ICSDashboard component
  const [currentAssumption, setCurrentAssumption] = useState({ assumption: '', description: '' });
  const [editingAssumptionId, setEditingAssumptionId] = useState(null); // Track which assumption is being edited
  const [currentArtificiality, setCurrentArtificiality] = useState({ artificiality: '', description: '' });
  const [editingArtificialityId, setEditingArtificialityId] = useState(null); // Track which artificiality is being edited
  const [currentSafetyConcern, setSafetyConcern] = useState({ 
    concern: '', description: '' 
  });
  const [editingSafetyConcernId, setEditingSafetyConcernId] = useState(null); // Track which safety concern is being edited

  // Validation helper functions for scenario section (using consistent validation)
  const validateScenarioLatitudeField = (lat) => {
    return validateLatitude(lat); // Use the comprehensive validation function
  };

  const validateScenarioLongitudeField = (lng) => {
    return validateLongitude(lng); // Use the comprehensive validation function
  };

  const handleScenarioLatitudeChange = (e) => {
    const lat = e.target.value;
    handleLatitudeChange(lat, 'scenario');
  };

  const handleScenarioLongitudeChange = (e) => {
    const lng = e.target.value;
    handleLongitudeChange(lng, 'scenario');
  };

  // Validation helper functions for event section
  const validateEventLatitudeField = (lat) => {
    if (lat === '' || lat === 0) return ''; // Field is not required
    if (!validateLatitude(lat)) {
      return 'Please enter latitude in format 45.1234 (range: -90.0000 to 90.0000)';
    }
    return '';
  };

  const validateEventLongitudeField = (lng) => {
    if (lng === '' || lng === 0) return ''; // Field is not required
    if (!validateLongitude(lng)) {
      return 'Please enter longitude in format 97.0000 (range: -180.0000 to 180.0000)';
    }
    return '';
  };

  const handleEventLatitudeChange = (e) => {
    const lat = e.target.value;
    setCurrentEvent(prev => ({ ...prev, latitude: lat }));
    
    // Real-time validation
    const error = validateEventLatitudeField(lat);
    setEventValidationErrors(prev => ({ ...prev, latitude: error }));
  };

  const handleEventLongitudeChange = (e) => {
    const lng = e.target.value;
    setCurrentEvent(prev => ({ ...prev, longitude: lng }));
    
    // Real-time validation
    const error = validateEventLongitudeField(lng);
    setEventValidationErrors(prev => ({ ...prev, longitude: error }));
  };

  // Validation helper functions for organization section
  const validateOrganizationLatitudeField = (lat) => {
    // Field is optional - if empty, no error
    if (lat === '' || lat === null || lat === undefined) return '';
    
    // Convert to number for validation
    const numLat = parseFloat(lat);
    
    // Check if it's a valid number
    if (isNaN(numLat)) {
      return 'Please enter a valid latitude number';
    }
    
    // Validate range: -90 to 90
    if (numLat < -90 || numLat > 90) {
      return 'Latitude must be between -90.0000 and 90.0000';
    }
    
    return '';
  };

  const validateOrganizationLongitudeField = (lng) => {
    // Field is optional - if empty, no error
    if (lng === '' || lng === null || lng === undefined) return '';
    
    // Convert to number for validation
    const numLng = parseFloat(lng);
    
    // Check if it's a valid number
    if (isNaN(numLng)) {
      return 'Please enter a valid longitude number';
    }
    
    // Validate range: -180 to 180
    if (numLng < -180 || numLng > 180) {
      return 'Longitude must be between -180.0000 and 180.0000';
    }
    
    return '';
  };

  const handleOrganizationLatitudeChange = (e) => {
    const lat = e.target.value;
    setCurrentOrganization(prev => ({ ...prev, latitude: lat }));
    
    // Real-time validation
    const error = validateOrganizationLatitudeField(lat);
    setOrganizationValidationErrors(prev => ({ ...prev, latitude: error }));
  };

  const handleOrganizationLongitudeChange = (e) => {
    const lng = e.target.value;
    setCurrentOrganization(prev => ({ ...prev, longitude: lng }));
    
    // Real-time validation
    const error = validateOrganizationLongitudeField(lng);
    setOrganizationValidationErrors(prev => ({ ...prev, longitude: error }));
  };

  // Load existing exercise data when editing
  useEffect(() => {
    if (editingExercise) {
      // Only treat this as an "edit" if the seed has an id — a seeded
      // record from the HIRA ExCon flow has no id and should still act
      // like a fresh Exercise Builder (POST on first save).
      setIsEditing(Boolean(editingExercise.id));
      
      // Convert ISO date strings back to date format for form inputs
      const startDate = editingExercise.start_date ? new Date(editingExercise.start_date) : null;
      const endDate = editingExercise.end_date ? new Date(editingExercise.end_date) : null;
      
      setExerciseData({
        id: editingExercise.id,
        exercise_image: editingExercise.exercise_image || null,
        exercise_name: editingExercise.exercise_name || '',
        exercise_type: editingExercise.exercise_type || '',
        exercise_description: editingExercise.exercise_description || '',
        location: editingExercise.location || '',
        latitude: editingExercise.latitude || '',
        longitude: editingExercise.longitude || '',
        zoom_level: editingExercise.zoom_level || 8,
        start_date: startDate ? startDate.toISOString().split('T')[0] : '',
        start_time: startDate ? startDate.toTimeString().slice(0, 5) : '',
        end_date: endDate ? endDate.toISOString().split('T')[0] : '',
        end_time: endDate ? endDate.toTimeString().slice(0, 5) : '',
        scope_description: editingExercise.scope_description || '',
        scope_hazards: editingExercise.scope_hazards || '',
        scope_geographic_area: editingExercise.scope_geographic_area || '',
        scope_functions: editingExercise.scope_functions || '',
        scope_organizations: editingExercise.scope_organizations || '',
        scope_personnel: editingExercise.scope_personnel || '',
        scope_exercise_type: editingExercise.scope_exercise_type || '',
        purpose_description: editingExercise.purpose_description || '',
        scenario_image: editingExercise.scenario_image || null,
        scenario_name: editingExercise.scenario_name || '',
        scenario_description: editingExercise.scenario_description || '',
        scenario_latitude: editingExercise.scenario_latitude || 0,
        scenario_longitude: editingExercise.scenario_longitude || 0
      });

      // Set image previews if they exist
      const previews = {};
      if (editingExercise.exercise_image) {
        previews.exercise_image = editingExercise.exercise_image;
      }
      if (editingExercise.scenario_image) {
        previews.scenario_image = editingExercise.scenario_image;
      }
      setImagePreview(previews);

      // Load dynamic collections if they exist
      console.log('Loading dynamic collections from editingExercise:', editingExercise);
      console.log('Full editingExercise object:', JSON.stringify(editingExercise, null, 2));
      console.log('Goals data:', editingExercise.goals);
      console.log('Goals type:', typeof editingExercise.goals);
      console.log('Goals is array:', Array.isArray(editingExercise.goals));
      console.log('Objectives data:', editingExercise.objectives);
      console.log('Objectives type:', typeof editingExercise.objectives);
      console.log('Objectives is array:', Array.isArray(editingExercise.objectives));
      
      // Always set the collections, even if they're empty arrays
      if (editingExercise.goals !== undefined) {
        console.log('Setting goals with data:', editingExercise.goals);
        // Migrate flat goals (with a `description` field) to two-tier shape:
        // each goal now carries an `objectives: [{ id, description, achieved }]` array.
        const migrated = (Array.isArray(editingExercise.goals) ? editingExercise.goals : []).map((g) => {
          const existing = Array.isArray(g.objectives) ? g.objectives : [];
          if (existing.length > 0) {
            return {
              ...g,
              objectives: existing.map((o) => ({
                id: o.id || `obj-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                name: o.name || '',
                description: o.description || '',
                achieved: o.achieved || null,
              })),
            };
          }
          const legacyDesc = (g.description || '').trim();
          return {
            ...g,
            objectives: legacyDesc
              ? [{ id: `obj-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: 'Objective', description: legacyDesc, achieved: null }]
              : [],
          };
        });
        setGoals(migrated);
      }
      // Silently drop the legacy top-level `objectives` list — those were
      // no longer surfaced in the merged Step 5 UI.
      setObjectives([]);
      if (editingExercise.events !== undefined) {
        setEvents(Array.isArray(editingExercise.events) ? editingExercise.events : []);
      }
      if (editingExercise.functions !== undefined) {
        setFunctions(Array.isArray(editingExercise.functions) ? editingExercise.functions : []);
      }
      if (editingExercise.organizations !== undefined) {
        setOrganizations(Array.isArray(editingExercise.organizations) ? editingExercise.organizations : []);
      }
      if (editingExercise.exercise_sites !== undefined) {
        setExerciseSites(Array.isArray(editingExercise.exercise_sites) ? editingExercise.exercise_sites : []);
      }
      if (editingExercise.codeWords !== undefined) {
        setCodeWords(Array.isArray(editingExercise.codeWords) ? editingExercise.codeWords : []);
      }
      if (editingExercise.callsigns !== undefined) {
        setCallsigns(Array.isArray(editingExercise.callsigns) ? editingExercise.callsigns : []);
      }
      if (editingExercise.frequencies !== undefined) {
        setFrequencies(Array.isArray(editingExercise.frequencies) ? editingExercise.frequencies : []);
      }
      if (editingExercise.assumptions !== undefined) {
        setAssumptions(Array.isArray(editingExercise.assumptions) ? editingExercise.assumptions : []);
      }
      if (editingExercise.artificialities !== undefined) {
        setArtificialities(Array.isArray(editingExercise.artificialities) ? editingExercise.artificialities : []);
      }
      if (editingExercise.safetyConcerns !== undefined) {
        setSafetyConcerns(Array.isArray(editingExercise.safetyConcerns) ? editingExercise.safetyConcerns : []);
      }
      if (editingExercise.standardsOfConduct !== undefined) {
        setStandardsOfConduct(Array.isArray(editingExercise.standardsOfConduct) ? editingExercise.standardsOfConduct : []);
      }
    }
  }, [editingExercise]);

  // Re-fetch organizations from the backend whenever the user enters Step 8.
  // The Liaison Officer → Organizations Coord view writes to the same
  // `exercise.organizations` array and auto-persists, so we need to pull the
  // latest here to avoid showing (and then overwriting with) stale state.
  useEffect(() => {
    if (currentStep !== 8) return;
    const exId = exerciseData?.id;
    if (!exId) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await axios.get(`${API}/exercise-builder/${exId}`, { headers });
        if (!cancelled && Array.isArray(r.data?.organizations)) {
          setOrganizations(r.data.organizations);
        }
      } catch (err) {
        console.warn('Step 8 organizations refresh failed:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentStep, exerciseData?.id, token]);

  const exerciseTypes = [
    'Table Top',
    'Drill', 
    'Functional',
    'Full Scale Exercise',
    'No-Notice Exercise',
    'Real World Event'
  ];

  const steps = [
    { number: 1, title: 'Exercise', description: 'Basic exercise information' },
    { number: 2, title: 'Scenario', description: 'Exercise scenario details' },
    { number: 3, title: 'Purpose', description: 'Exercise purpose and intent' },
    { number: 4, title: 'Scope', description: 'Define exercise scope and boundaries' },
    { number: 5, title: 'Goals & Objectives', description: 'Exercise goals and specific objectives to test' },
    { number: 6, title: 'Functions', description: 'Functions to be exercised' },
    { number: 7, title: 'Injections', description: 'MSEL integration' },
    { number: 8, title: 'Organizations', description: 'Participating organizations' },
    { number: 9, title: 'Exercise Sites', description: 'Physical sites for the exercise' },
    { number: 10, title: 'Team Coordinators', description: 'Exercise coordination team' },
    { number: 11, title: 'Code Words', description: 'Exercise code words' },
    { number: 12, title: 'Callsigns', description: 'Communication callsigns' },
    { number: 13, title: 'Assumptions', description: 'Exercise assumptions' },
    { number: 14, title: 'Artificialities', description: 'Artificial constraints' },
    { number: 15, title: 'Safety', description: 'Safety considerations' },
    { number: 16, title: 'Standard of Conduct', description: 'Expected standards and conduct' },
    { number: 17, title: 'Mapping', description: 'Create map objects for exercise' }
  ];

  const handleImageUpload = (event, imageType) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setImagePreview(prev => ({ ...prev, [imageType]: imageData }));
        setExerciseData(prev => ({ ...prev, [imageType]: imageData }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = async (imageType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg');
        setImagePreview(prev => ({ ...prev, [imageType]: imageData }));
        setExerciseData(prev => ({ ...prev, [imageType]: imageData }));
        
        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please use file upload instead.');
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      // Scroll to top of the page when moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top of the page when moving to previous step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Add handlers for dynamic items
  const addGoal = () => {
    if (currentGoal.name.trim()) {
      if (editingGoalId) {
        // Update existing goal (preserve its nested objectives)
        setGoals(prev => prev.map(item => 
          item.id === editingGoalId 
            ? {
                ...item,
                name: currentGoal.name,
                broad_statement: currentGoal.broad_statement || '',
                id: editingGoalId,
              }
            : item
        ));
        setEditingGoalId(null);
      } else {
        // Add new goal with empty objectives list
        setGoals(prev => [...prev, {
          id: Date.now(),
          name: currentGoal.name,
          broad_statement: currentGoal.broad_statement || '',
          objectives: [],
        }]);
      }
      setCurrentGoal({ name: '', broad_statement: '', description: '' });
    }
  };

  const editGoal = (goal) => {
    setCurrentGoal({
      name: goal.name,
      broad_statement: goal.broad_statement || '',
      description: '', // no longer editable on the goal
    });
    setEditingGoalId(goal.id);
  };

  const cancelGoalEdit = () => {
    setCurrentGoal({ name: '', broad_statement: '', description: '' });
    setEditingGoalId(null);
  };

  const addObjective = () => {
    if (currentObjective.name.trim()) {
      if (editingObjectiveId) {
        // Update existing objective
        setObjectives(prev => prev.map(obj => 
          obj.id === editingObjectiveId 
            ? { ...currentObjective, id: editingObjectiveId }
            : obj
        ));
        setEditingObjectiveId(null);
      } else {
        // Add new objective
        setObjectives(prev => [...prev, { ...currentObjective, id: Date.now() }]);
      }
      setCurrentObjective({ name: '', description: '' });
    }
  };

  const editObjective = (objective) => {
    setCurrentObjective({
      name: objective.name,
      description: objective.description,
    });
    setEditingObjectiveId(objective.id);
  };

  const cancelObjectiveEdit = () => {
    setCurrentObjective({ name: '', description: '' });
    setEditingObjectiveId(null);
  };

  const addEvent = () => {
    if (currentEvent.name.trim()) {
      setEvents(prev => [...prev, { ...currentEvent, id: Date.now() }]);
      setCurrentEvent({ 
        name: '', description: '', actions: '', latitude: 0, longitude: 0, 
        start_date: '', start_time: '', end_date: '', end_time: '', 
        tier_scale: '', escalation_value: 'none' 
      });
    }
  };

  const addFunction = () => {
    if (currentFunction.name.trim()) {
      if (editingFunctionId) {
        // Update existing function
        setFunctions(prev => prev.map(item => 
          item.id === editingFunctionId 
            ? { ...currentFunction, id: editingFunctionId }
            : item
        ));
        setEditingFunctionId(null);
      } else {
        // Add new function
        setFunctions(prev => [...prev, { ...currentFunction, id: Date.now() }]);
      }
      setCurrentFunction({ name: '', description: '' });
    }
  };

  const editFunction = (func) => {
    setCurrentFunction({
      name: func.name,
      description: func.description,
    });
    setEditingFunctionId(func.id);
  };

  const cancelFunctionEdit = () => {
    setCurrentFunction({ name: '', description: '' });
    setEditingFunctionId(null);
  };

  // Persist the organizations array to the backend right away so image edits
  // don't get lost if the user changes step or navigates elsewhere (matches
  // the immediate-persist behaviour of Liaison Officer → Organizations Coord).
  const persistOrganizations = async (nextOrgs) => {
    if (!exerciseData.id) return; // No exercise yet — will save with Save Step
    try {
      await axios.put(
        `${API}/exercise-builder/${exerciseData.id}`,
        { organizations: nextOrgs },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
    } catch (err) {
      console.error('Auto-persist organizations failed:', err);
      toast.error('Failed to save organization change: ' + (err.response?.data?.detail || err.message));
    }
  };

  const addOrganization = () => {
    if (currentOrganization.name.trim()) {
      // Validate cell phone if provided
      const phone = currentOrganization.contact_phone || '';
      if (phone) {
        const digits = phone.replace(/\D/g, '');
        if (digits.length !== 10) {
          window.alert('Contact Cell Phone must be a valid 10-digit phone number (e.g. 123-456-7890)');
          return;
        }
      }
      let nextOrgs;
      if (editingOrganizationId) {
        // Update existing organization — spread the existing item first so
        // fields not tracked by `currentOrganization` (notably `persons`)
        // are preserved across image / name / description edits.
        nextOrgs = organizations.map(item =>
          item.id === editingOrganizationId
            ? { ...item, ...currentOrganization, id: editingOrganizationId }
            : item
        );
        setEditingOrganizationId(null);
      } else {
        // Add new organization
        nextOrgs = [...organizations, { ...currentOrganization, id: Date.now(), persons: [] }];
      }
      setOrganizations(nextOrgs);
      persistOrganizations(nextOrgs);
      setCurrentOrganization({ 
        name: '', description: '', home_base: '', latitude: '', longitude: '',
        contact_first_name: '', contact_last_name: '', contact_phone: '', contact_email: '',
        organization_image: null
      });
      // Clear validation errors and image preview
      setOrganizationValidationErrors({ latitude: '', longitude: '' });
      setOrganizationImagePreview(null);
    }
  };

  const editOrganization = (org) => {
    setCurrentOrganization({
      name: org.name,
      description: org.description || '',
      home_base: org.home_base || '',
      latitude: org.latitude || '',
      longitude: org.longitude || '',
      contact_first_name: org.contact_first_name || '',
      contact_last_name: org.contact_last_name || '',
      contact_phone: org.contact_phone || '',
      contact_email: org.contact_email || '',
      organization_image: org.organization_image || null
    });
    setEditingOrganizationId(org.id);
    setOrganizationImagePreview(org.organization_image || null);
  };

  const cancelOrganizationEdit = () => {
    setCurrentOrganization({ 
      name: '', description: '', home_base: '', latitude: '', longitude: '',
      contact_first_name: '', contact_last_name: '', contact_phone: '', contact_email: '',
      organization_image: null
    });
    setEditingOrganizationId(null);
    setOrganizationValidationErrors({ latitude: '', longitude: '' });
    setOrganizationImagePreview(null);
  };

  // Organization image upload handler
  const handleOrganizationImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setOrganizationImagePreview(imageData);
        setCurrentOrganization(prev => ({ ...prev, organization_image: imageData }));
      };
      reader.readAsDataURL(file);
    }
  };

  const { dropProps: orgImageDropProps, isDragging: isOrgImageDragging } = useImageDrop({
    onFile: (file) => handleOrganizationImageUpload({ target: { files: [file] } })
  });

  // Organization camera capture handler
  const handleOrganizationCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg');
        setOrganizationImagePreview(imageData);
        setCurrentOrganization(prev => ({ ...prev, organization_image: imageData }));
        
        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please use file upload instead.');
    }
  };

  const addCodeWord = () => {
    if (currentCodeWord.word.trim()) {
      if (editingCodeWordId) {
        // Update existing code word
        setCodeWords(prev => prev.map(item => 
          item.id === editingCodeWordId 
            ? { ...currentCodeWord, id: editingCodeWordId }
            : item
        ));
        setEditingCodeWordId(null);
      } else {
        // Add new code word
        setCodeWords(prev => [...prev, { ...currentCodeWord, id: Date.now() }]);
      }
      setCurrentCodeWord({ word: '', meaning: '' });
    }
  };

  const editCodeWord = (codeWord) => {
    setCurrentCodeWord({
      word: codeWord.word,
      meaning: codeWord.meaning || ''
    });
    setEditingCodeWordId(codeWord.id);
  };

  const cancelCodeWordEdit = () => {
    setCurrentCodeWord({ word: '', meaning: '' });
    setEditingCodeWordId(null);
  };

  const addCallsign = () => {
    if (currentCallsign.callsign.trim()) {
      if (editingCallsignId) {
        // Update existing callsign
        setCallsigns(prev => prev.map(item => 
          item.id === editingCallsignId 
            ? { ...currentCallsign, id: editingCallsignId }
            : item
        ));
        setEditingCallsignId(null);
      } else {
        // Add new callsign
        setCallsigns(prev => [...prev, { ...currentCallsign, id: Date.now() }]);
      }
      setCurrentCallsign({ callsign: '', description: '' });
    }
  };

  const editCallsign = (callsign) => {
    setCurrentCallsign({
      callsign: callsign.callsign,
      description: callsign.description || ''
    });
    setEditingCallsignId(callsign.id);
  };

  const cancelCallsignEdit = () => {
    setCurrentCallsign({ callsign: '', description: '' });
    setEditingCallsignId(null);
  };

  const addFrequency = () => {
    if (currentFrequency.frequency.trim()) {
      if (editingFrequencyId) {
        // Update existing frequency
        setFrequencies(prev => prev.map(item => 
          item.id === editingFrequencyId 
            ? { ...currentFrequency, id: editingFrequencyId }
            : item
        ));
        setEditingFrequencyId(null);
      } else {
        // Add new frequency
        setFrequencies(prev => [...prev, { ...currentFrequency, id: Date.now() }]);
      }
      setCurrentFrequency({ 
        frequency: '', type: '', description: '', primary_backup: 'Primary', modulation_type: '' 
      });
    }
  };

  const editFrequency = (freq) => {
    setCurrentFrequency({
      frequency: freq.frequency,
      type: freq.type || '',
      description: freq.description || '',
      primary_backup: freq.primary_backup || 'Primary',
      tone: freq.tone || '',
      offset: freq.offset || '',
      channel: freq.channel || '',
      radio_type: freq.radio_type || '',
      modulation_type: freq.modulation_type || ''
    });
    setEditingFrequencyId(freq.id);
  };

  const cancelFrequencyEdit = () => {
    setCurrentFrequency({ 
      frequency: '', type: '', description: '', primary_backup: 'Primary',
      tone: '', offset: '', channel: '', radio_type: '', modulation_type: ''
    });
    setEditingFrequencyId(null);
  };

  // ICS 205 Communication Plan functions moved to ICSDashboard component

  const addAssumption = () => {
    if (currentAssumption.assumption.trim()) {
      if (editingAssumptionId) {
        // Update existing assumption
        setAssumptions(prev => prev.map(item => 
          item.id === editingAssumptionId 
            ? { ...currentAssumption, id: editingAssumptionId }
            : item
        ));
        setEditingAssumptionId(null);
      } else {
        // Add new assumption
        setAssumptions(prev => [...prev, { ...currentAssumption, id: Date.now() }]);
      }
      setCurrentAssumption({ assumption: '', description: '' });
    }
  };

  const editAssumption = (assumption) => {
    setCurrentAssumption({
      assumption: assumption.assumption,
      description: assumption.description || ''
    });
    setEditingAssumptionId(assumption.id);
  };

  const cancelAssumptionEdit = () => {
    setCurrentAssumption({ assumption: '', description: '' });
    setEditingAssumptionId(null);
  };

  const addArtificiality = () => {
    if (currentArtificiality.artificiality.trim()) {
      if (editingArtificialityId) {
        // Update existing artificiality
        setArtificialities(prev => prev.map(item => 
          item.id === editingArtificialityId 
            ? { ...currentArtificiality, id: editingArtificialityId }
            : item
        ));
        setEditingArtificialityId(null);
      } else {
        // Add new artificiality
        setArtificialities(prev => [...prev, { ...currentArtificiality, id: Date.now() }]);
      }
      setCurrentArtificiality({ artificiality: '', description: '' });
    }
  };

  const editArtificiality = (artificiality) => {
    setCurrentArtificiality({
      artificiality: artificiality.artificiality,
      description: artificiality.description || ''
    });
    setEditingArtificialityId(artificiality.id);
  };

  const cancelArtificialityEdit = () => {
    setCurrentArtificiality({ artificiality: '', description: '' });
    setEditingArtificialityId(null);
  };

  const addSafetyConcern = () => {
    if (currentSafetyConcern.concern.trim()) {
      if (editingSafetyConcernId) {
        // Update existing safety concern
        setSafetyConcerns(prev => prev.map(item => 
          item.id === editingSafetyConcernId 
            ? { ...currentSafetyConcern, id: editingSafetyConcernId }
            : item
        ));
        setEditingSafetyConcernId(null);
      } else {
        // Add new safety concern
        setSafetyConcerns(prev => [...prev, { ...currentSafetyConcern, id: Date.now() }]);
      }
      setSafetyConcern({ 
        concern: '', description: '' 
      });
    }
  };

  const editSafetyConcern = (safetyConcern) => {
    setSafetyConcern({
      concern: safetyConcern.concern,
      description: safetyConcern.description || ''
    });
    setEditingSafetyConcernId(safetyConcern.id);
  };

  const cancelSafetyConcernEdit = () => {
    setSafetyConcern({ 
      concern: '', description: '' 
    });
    setEditingSafetyConcernId(null);
  };

  // Remove handlers
  const removeGoal = (id) => setGoals(prev => prev.filter(item => item.id !== id));
  
  // ── Nested objectives handlers (two-tier hierarchy) ───────────────
  const _newObjId = () => `obj-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  
  const addObjectiveToGoal = (goalId, name, description) => {
    const n = (name || '').trim();
    if (!n) return;
    setGoals(prev => prev.map(g =>
      g.id === goalId
        ? {
            ...g,
            objectives: [
              ...(g.objectives || []),
              { id: _newObjId(), name: n, description: (description || '').trim(), achieved: null },
            ],
          }
        : g
    ));
  };
  const updateObjectiveField = (goalId, objId, field, value) => {
    setGoals(prev => prev.map(g =>
      g.id === goalId
        ? { ...g, objectives: (g.objectives || []).map(o => o.id === objId ? { ...o, [field]: value } : o) }
        : g
    ));
  };
  const removeObjectiveFromGoal = (goalId, objId) => {
    setGoals(prev => prev.map(g =>
      g.id === goalId
        ? { ...g, objectives: (g.objectives || []).filter(o => o.id !== objId) }
        : g
    ));
  };
  const reorderObjectivesInGoal = (goalId, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      const arr = [...(g.objectives || [])];
      if (fromIdx < 0 || fromIdx >= arr.length || toIdx < 0 || toIdx >= arr.length) return g;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return { ...g, objectives: arr };
    }));
  };
  const removeObjective = (id) => setObjectives(prev => prev.filter(item => item.id !== id));
  const removeEvent = (id) => setEvents(prev => prev.filter(item => item.id !== id));
  const removeFunction = (id) => setFunctions(prev => prev.filter(item => item.id !== id));
  const removeOrganization = (id) => {
    const nextOrgs = organizations.filter(item => item.id !== id);
    setOrganizations(nextOrgs);
    persistOrganizations(nextOrgs);
  };
  const removeCodeWord = (id) => setCodeWords(prev => prev.filter(item => item.id !== id));
  const removeCallsign = (id) => setCallsigns(prev => prev.filter(item => item.id !== id));
  const removeFrequency = (id) => setFrequencies(prev => prev.filter(item => item.id !== id));
  // removeIcs205Frequency moved to ICSDashboard component
  const removeAssumption = (id) => setAssumptions(prev => prev.filter(item => item.id !== id));
  const removeArtificiality = (id) => setArtificialities(prev => prev.filter(item => item.id !== id));
  const removeSafetyConcern = (id) => setSafetyConcerns(prev => prev.filter(item => item.id !== id));

  // Load coordinators from participants
  // Criteria: position = "Team Coordinator" AND assigned_exercise = current exercise AND involvedInExercise = true
  const loadCoordinators = async () => {
    try {
      setLoading(true);
      
      // Get current exercise ID and name - check both prop and state
      const currentExerciseId = editingExercise?.id || exerciseData?.id;
      const currentExerciseName = editingExercise?.exercise_name || exerciseData?.exercise_name;
      
      console.log('🔍 Loading Team Coordinators');
      console.log('   editingExercise:', editingExercise ? { id: editingExercise.id, name: editingExercise.exercise_name } : 'null');
      console.log('   exerciseData:', exerciseData ? { id: exerciseData.id, name: exerciseData.exercise_name } : 'null');
      console.log('   Using: { id:', currentExerciseId, ', name:', currentExerciseName, '}');
      
      if (!currentExerciseId && !currentExerciseName) {
        console.log('⚠️ No exercise ID or name available');
        alert('Please save the exercise first before loading coordinators.');
        return;
      }
      
      // Fetch all participants
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/participants`, { headers });
      const participants = response.data;
      
      console.log(`📋 Total participants fetched: ${participants.length}`);
      
      // Filter participants who:
      // 1. Have position "Team Coordinator"
      // 2. Assigned Exercise equals current exercise (check by ID or name)
      // 3. Are participating in current exercise (involvedInExercise = true)
      const eligibleCoordinators = participants.filter(participant => {
        const positionMatch = participant.position === 'Team Coordinator';
        
        // Check exercise match by ID or name
        // Check assigned exercise - prefer assigned_exercise field, fallback to assignedTo
        const assignedExerciseValue = participant.assigned_exercise || participant.assignedExercise || '';
        const assignedToValue = participant.assignedTo || '';
        
        // Match either the assigned_exercise field or the assignedTo field (for backwards compatibility)
        const exerciseMatch = (currentExerciseId && (assignedExerciseValue === currentExerciseId || assignedToValue === currentExerciseId)) || 
                             (currentExerciseName && (assignedExerciseValue === currentExerciseName || assignedToValue === currentExerciseName));
        
        const involvedMatch = participant.involvedInExercise === true;
        
        // Debug logging for all participants with Team Coordinator position
        if (positionMatch) {
          console.log(`🔎 Found Team Coordinator: ${participant.firstName} ${participant.lastName}`);
          console.log(`   - Position: "${participant.position}" ✓`);
          console.log(`   - assigned_exercise: "${assignedExerciseValue}"`);
          console.log(`   - assignedTo: "${assignedToValue}"`);
          console.log(`   - Exercise ID: "${currentExerciseId}" | Name: "${currentExerciseName}"`);
          console.log(`   - Exercise Match: ${exerciseMatch ? '✓' : '✗'}`);
          console.log(`   - Involved: ${participant.involvedInExercise} = ${involvedMatch ? '✓' : '✗'}`);
          console.log(`   - ALL CRITERIA MET: ${positionMatch && exerciseMatch && involvedMatch ? '✓ YES' : '✗ NO'}`);
        }
        
        return positionMatch && exerciseMatch && involvedMatch;
      });
      
      // Transform participants to coordinator format
      const coordinators = eligibleCoordinators.map(participant => ({
        id: participant.id || Date.now() + Math.random(),
        name: `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || participant.name || 'Unknown',
        firstName: participant.firstName || '',
        lastName: participant.lastName || '',
        role: participant.position || 'Team Coordinator',
        email: participant.email || '',
        phone: participant.cellPhone || participant.homePhone || participant.phone || '',
        cellPhone: participant.cellPhone || '',
        organization: participant.organization || '',
        assignedTo: participant.assignedTo || participant.assigned_exercise || ''
      }));
      
      // Update exerciseData with loaded coordinators
      setExerciseData(prev => ({
        ...prev,
        coordinators: coordinators
      }));
      
      if (coordinators.length > 0) {
        console.log(`✅ Loaded ${coordinators.length} Team Coordinators:`, coordinators.map(c => c.name));
      } else {
        console.log('ℹ️ No Team Coordinators found matching all criteria');
      }
      
    } catch (error) {
      console.error('Error loading coordinators:', error);
      alert('Failed to load coordinators. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load Safety Officer from participants
  // Criteria: position = "Command - Safety Officer" AND assigned_exercise = current exercise AND involvedInExercise = true
  const loadSafetyOfficer = async () => {
    try {
      setSafetyOfficerLoading(true);
      
      // Get current exercise ID and name - use exerciseData which is populated from editingExercise prop
      const currentExerciseId = exerciseData?.id;
      const currentExerciseName = exerciseData?.exercise_name;
      
      console.log('🔍 Loading Safety Officer');
      console.log('   exerciseData:', exerciseData ? { id: exerciseData.id, name: exerciseData.exercise_name } : 'null');
      console.log('   Using: { id:', currentExerciseId, ', name:', currentExerciseName, '}');
      
      if (!currentExerciseId && !currentExerciseName) {
        console.log('⚠️ No exercise ID or name available');
        setSafetyOfficer({ firstName: '', lastName: '', cellPhone: '', participantId: null });
        return;
      }
      
      // Fetch all participants
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/participants`, { headers });
      const participants = response.data;
      
      console.log(`📋 Total participants fetched: ${participants.length}`);
      
      // Find safety officer matching criteria:
      // 1. Position equals "Command - Safety Officer"
      // 2. Assigned Exercise equals current exercise (check by ID or name)
      // 3. involvedInExercise checkbox is checked (true)
      const safetyOfficerParticipant = participants.find(participant => {
        const positionMatch = participant.position === 'Command - Safety Officer';
        
        // Check exercise match by ID or name
        // Check assigned exercise - prefer assigned_exercise field, fallback to assignedTo
        const assignedExerciseValue = participant.assigned_exercise || participant.assignedExercise || '';
        const assignedToValue = participant.assignedTo || '';
        
        // Match either the assigned_exercise field or the assignedTo field (for backwards compatibility)
        const exerciseMatch = (currentExerciseId && (assignedExerciseValue === currentExerciseId || assignedToValue === currentExerciseId)) || 
                             (currentExerciseName && (assignedExerciseValue === currentExerciseName || assignedToValue === currentExerciseName));
        
        const involvedMatch = participant.involvedInExercise === true;
        
        // Debug logging for Safety Officers
        if (positionMatch) {
          console.log(`🔎 Found Safety Officer: ${participant.firstName} ${participant.lastName}`);
          console.log(`   - Position: "${participant.position}" ✓`);
          console.log(`   - assigned_exercise: "${assignedExerciseValue}"`);
          console.log(`   - assignedTo: "${assignedToValue}"`);
          console.log(`   - Exercise ID: "${currentExerciseId}" | Name: "${currentExerciseName}"`);
          console.log(`   - Exercise Match: ${exerciseMatch ? '✓' : '✗'}`);
          console.log(`   - Involved: ${participant.involvedInExercise} = ${involvedMatch ? '✓' : '✗'}`);
          console.log(`   - ALL CRITERIA MET: ${positionMatch && exerciseMatch && involvedMatch ? '✓ YES' : '✗ NO'}`);
        }
        
        return positionMatch && exerciseMatch && involvedMatch;
      });
      
      if (safetyOfficerParticipant) {
        setSafetyOfficer({
          firstName: safetyOfficerParticipant.firstName || '',
          lastName: safetyOfficerParticipant.lastName || '',
          cellPhone: safetyOfficerParticipant.cellPhone || safetyOfficerParticipant.phone || '',
          participantId: safetyOfficerParticipant.id
        });
        console.log('✅ Safety Officer loaded:', safetyOfficerParticipant.firstName, safetyOfficerParticipant.lastName);
      } else {
        setSafetyOfficer({ firstName: '', lastName: '', cellPhone: '', participantId: null });
        console.log('ℹ️ No Safety Officer found matching all criteria');
      }
      
    } catch (error) {
      console.error('Error loading safety officer:', error);
      setSafetyOfficer({ firstName: '', lastName: '', cellPhone: '', participantId: null });
    } finally {
      setSafetyOfficerLoading(false);
    }
  };

  // Auto-load Safety Officer when entering Step 17 (Mapping)
  useEffect(() => {
    if (currentStep === 17) {
      loadSafetyOfficer();
    }
  }, [currentStep, editingExercise?.id, exerciseData?.id]);

  // Auto-save the wizard every 60 seconds while editing an existing exercise.
  // Only runs once the exercise has an id (i.e. user has hit Next/Save at least
  // once), so we never accidentally create duplicate exercises via silent posts.
  useEffect(() => {
    if (!exerciseData?.id) return undefined;
    const timer = setInterval(async () => {
      // Skip if another save is in flight or the form is loading
      if (loading || autoSaving) return;
      try {
        setAutoSaving(true);
        await saveExercise({ stayOnStep: true, silent: true });
        setLastAutoSaved(new Date());
      } catch (err) {
        console.warn('[auto-save] failed:', err?.message || err);
      } finally {
        setAutoSaving(false);
      }
    }, 60000); // 60s
    return () => clearInterval(timer);
  }, [exerciseData?.id, loading]);

  // Save current step as draft
  const saveStepDraft = async () => {
    setLoading(true);
    try {
      // Combine all current data
      const allData = {
        ...exerciseData,
        goals,
        objectives,
        events,
        functions,
        organizations,
        exercise_sites: exerciseSites,
        codeWords,
        callsigns,
        frequencies,
        assumptions,
        artificialities,
        safetyConcerns,
        standardsOfConduct
      };

      console.log('Current form data before validation:', allData);

      // Validate required fields for Step 1
      if (!allData.exercise_name || allData.exercise_name.trim() === '') {
        throw new Error('Exercise name is required');
      }
      if (!allData.exercise_type || allData.exercise_type.trim() === '') {
        throw new Error('Exercise type is required');
      }
      if (!allData.exercise_description || allData.exercise_description.trim() === '') {
        throw new Error('Exercise description is required');
      }
      if (!allData.location || allData.location.trim() === '') {
        throw new Error('Location is required');
      }
      
      // Validate Latitude (required)
      if (!allData.latitude || allData.latitude === '') {
        setExerciseValidationErrors(prev => ({ ...prev, latitude: 'Latitude is required' }));
        throw new Error('Latitude is required');
      }
      const lat = parseFloat(allData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setExerciseValidationErrors(prev => ({ ...prev, latitude: 'Latitude must be between -90 and 90' }));
        throw new Error('Latitude must be between -90 and 90');
      }
      
      // Validate Longitude (required)
      if (!allData.longitude || allData.longitude === '') {
        setExerciseValidationErrors(prev => ({ ...prev, longitude: 'Longitude is required' }));
        throw new Error('Longitude is required');
      }
      const lng = parseFloat(allData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setExerciseValidationErrors(prev => ({ ...prev, longitude: 'Longitude must be between -180 and 180' }));
        throw new Error('Longitude must be between -180 and 180');
      }
      
      // Validate Zoom Level (required)
      if (!allData.zoom_level || allData.zoom_level === '') {
        setExerciseValidationErrors(prev => ({ ...prev, zoom_level: 'Zoom level is required' }));
        throw new Error('Zoom level is required');
      }
      const zoom = parseFloat(allData.zoom_level);
      if (isNaN(zoom) || zoom < 1 || zoom > 20) {
        setExerciseValidationErrors(prev => ({ ...prev, zoom_level: 'Zoom level must be between 1 and 20' }));
        throw new Error('Zoom level must be between 1 and 20');
      }

      // Validate required fields for Step 2 (Scenario)
      if (currentStep >= 2) {
        if (!allData.scenario_name || allData.scenario_name.trim() === '') {
          throw new Error('Scenario Name is required');
        }
      }

      // Validate required fields for Step 3 (Purpose)
      if (currentStep >= 3) {
        if (!allData.purpose_description || allData.purpose_description.trim() === '') {
          throw new Error('Purpose Description is required');
        }
      }

      // Validate required fields for Step 4 (Scope)
      if (currentStep >= 4) {
        if (!allData.scope_description || allData.scope_description.trim() === '') {
          throw new Error('Scope Description is required');
        }
      }

      // Handle dates with proper validation and defaults
      let startDate, endDate;
      
      // Check if user has provided date values (not empty strings from initial state)
      const hasStartDate = allData.start_date && allData.start_date.trim() !== '';
      const hasEndDate = allData.end_date && allData.end_date.trim() !== '';
      
      console.log('Date field status:', { hasStartDate, hasEndDate, start_date: allData.start_date, end_date: allData.end_date });
      
      if (hasStartDate) {
        const startTimeStr = allData.start_time && allData.start_time.trim() !== '' ? allData.start_time : '09:00';
        startDate = new Date(allData.start_date + 'T' + startTimeStr + ':00');
        if (isNaN(startDate.getTime())) {
          throw new Error('Invalid start date format. Please use a valid date.');
        }
      } else {
        // Default to tomorrow at 9 AM if no start date provided
        startDate = new Date();
        startDate.setDate(startDate.getDate() + 1);
        startDate.setHours(9, 0, 0, 0);
        console.log('Using default start date:', startDate);
      }
      
      if (hasEndDate) {
        const endTimeStr = allData.end_time && allData.end_time.trim() !== '' ? allData.end_time : '17:00';
        endDate = new Date(allData.end_date + 'T' + endTimeStr + ':00');
        if (isNaN(endDate.getTime())) {
          throw new Error('Invalid end date format. Please use a valid date.');
        }
      } else {
        // Default to same day as start date at 5 PM if no end date provided
        endDate = new Date(startDate);
        endDate.setHours(17, 0, 0, 0);
        console.log('Using default end date:', endDate);
      }
      
      // Ensure end date is after start date
      if (endDate <= startDate) {
        endDate = new Date(startDate);
        endDate.setHours(startDate.getHours() + 8); // Add 8 hours
        console.log('Adjusted end date to be after start date:', endDate);
      }
      
      const exercisePayload = {
        ...allData,
        start_date: startDate.toISOString(),
        start_time: startDate.toTimeString().substring(0, 5), // HH:MM format
        end_date: endDate.toISOString(),
        end_time: endDate.toTimeString().substring(0, 5), // HH:MM format
        scope_exercise_type: allData.exercise_type || allData.exercise_type
      };
      
      const payloadForAPI = { ...exercisePayload };
      delete payloadForAPI.id;
      
      console.log('Sending payload to backend:', payloadForAPI);
      
      if (isEditing && exerciseData.id) {
        await axios.put(
          `${API}/exercise-builder/${exerciseData.id}`, 
          payloadForAPI,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        toast.success(`Step ${currentStep} saved successfully!`);
      } else {
        const response = await axios.post(
          `${API}/exercise-builder`, 
          payloadForAPI,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        // Update the exercise ID for future saves
        if (response.data.id) {
          setExerciseData(prev => ({ ...prev, id: response.data.id }));
          setIsEditing(true);
        }
        toast.success(`Step ${currentStep} saved as draft!`);
      }
    } catch (error) {
      console.error('Error saving step:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response && error.response.status === 422) {
        // Extract detailed validation errors
        const validationErrors = error.response.data?.detail;
        if (Array.isArray(validationErrors)) {
          const errorMessages = validationErrors.map(err => `${err.loc?.join('.')} ${err.msg}`).join(', ');
          toast.error(`Validation Errors: ${errorMessages}`);
        } else {
          toast.error(`Validation Error: ${validationErrors || 'Please check all required fields and ensure dates are properly set'}`);
        }
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        toast.error('Network Error: Unable to connect to server. Please check your internet connection and try again.');
      } else if (error.response) {
        toast.error(`Server Error (${error.response.status}): ${error.response.data?.message || error.message}`);
      } else {
        toast.error(`Error saving step: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveExercise = async (options = {}) => {
    const stayOnStep = options?.stayOnStep === true;
    const silent = options?.silent === true;
    setLoading(true);
    try {
      // Validate and convert date strings to proper datetime format
      let startDate, endDate;
      
      if (exerciseData.start_date) {
        startDate = new Date(exerciseData.start_date + 'T' + (exerciseData.start_time || '00:00'));
        if (isNaN(startDate.getTime())) {
          throw new Error('Invalid start date');
        }
      } else {
        // Use current date if no start date provided
        startDate = new Date();
      }
      
      if (exerciseData.end_date) {
        endDate = new Date(exerciseData.end_date + 'T' + (exerciseData.end_time || '23:59'));
        if (isNaN(endDate.getTime())) {
          throw new Error('Invalid end date');
        }
      } else {
        // Use start date + 1 day if no end date provided
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }
      
      // Log debug information about data being saved
      console.log('💾 Saving Exercise with Complete Data:');
      console.log('📝 Basic Exercise Data:', exerciseData);
      console.log('🎯 Goals:', goals);
      console.log('🔸 Objectives:', objectives);
      console.log('📅 Events:', events);
      console.log('⚙️ Functions:', functions);
      console.log('🏢 Organizations:', organizations);
      
      const exercisePayload = {
        ...exerciseData,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        scope_exercise_type: exerciseData.exercise_type,
        // Add all dynamic collections
        goals: goals,
        objectives: objectives,
        events: events,
        functions: functions,
        organizations: organizations,
        exercise_sites: exerciseSites,
        codeWords: codeWords,
        callsigns: callsigns,
        frequencies: frequencies,
        assumptions: assumptions,
        artificialities: artificialities,
        safetyConcerns: safetyConcerns,
        standardsOfConduct: standardsOfConduct,
      };
      
      console.log('📦 Complete Payload Being Sent:', exercisePayload);
      
      // Remove id from payload for create operations, but keep it for updates
      const payloadForAPI = { ...exercisePayload };
      delete payloadForAPI.id;
      
      if (isEditing && exerciseData.id) {
        // Update existing exercise
        await axios.put(
          `${API}/exercise-builder/${exerciseData.id}`, 
          payloadForAPI,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        if (!silent) {
          toast.success(stayOnStep ? 'Draft saved' : 'Exercise updated successfully!');
        }
      } else {
        // Create new exercise
        const response = await axios.post(
          `${API}/exercise-builder`, 
          payloadForAPI,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        // Capture the new ID so subsequent Save Draft clicks UPDATE this
        // exercise instead of creating duplicates.
        if (response.data?.id) {
          setExerciseData(prev => ({ ...prev, id: response.data.id }));
          setIsEditing(true);
        }
        if (!silent) {
          toast.success(stayOnStep ? 'Draft saved' : 'Exercise saved successfully!');
        }
      }
      
      // If the user chose "Save Draft", keep them on the current step
      // instead of returning to Exercise Management.
      if (stayOnStep) {
        return;
      }

      // Clear URL parameters after saving and go back
      if (isEditing) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      onBack();
    } catch (error) {
      console.error('Error saving exercise:', error);
      toast.error('Error saving exercise. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ImageUploadComponent = ({ imageType, title }) => {
    const { dropProps, isDragging } = useImageDrop({
      onFile: (file) => handleImageUpload({ target: { files: [file] } }, imageType)
    });
    return (
    <div className="flex items-center space-x-6">
      <div
        {...dropProps}
        onClick={() => document.getElementById(`${imageType}-upload`)?.click()}
        data-testid={`${imageType}-dropzone`}
        title="Click to browse or drop an image here"
        className={`w-32 h-32 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
          isDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
        }`}
      >
        {imagePreview[imageType] ? (
          <img src={imagePreview[imageType]} alt={title} className="w-full h-full object-cover rounded-lg" />
        ) : (
          <div className="text-center px-2">
            <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <span className="text-xs text-gray-400">{isDragging ? 'Drop to upload' : 'No image · Drop here'}</span>
          </div>
        )}
      </div>
      <div className="space-y-3">
        <div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, imageType)}
            className="hidden"
            id={`${imageType}-upload`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => document.getElementById(`${imageType}-upload`).click()}
            className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10 mr-3"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Photo
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleCameraCapture(imageType)}
            className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
          >
            <Camera className="h-4 w-4 mr-2" />
            Take Photo
          </Button>
        </div>
        <p className="text-sm text-gray-400">Upload image, capture with camera, or drag &amp; drop onto the image area</p>
      </div>
    </div>
  );
  };

  const handlePrintExercise = async () => {
    try {
      console.log('=== PRINT FUNCTION START ===');
      console.log('Exercise data:', exerciseData);
      console.log('Exercise ID:', exerciseData.id);
      console.log('Exercise Name:', exerciseData.exercise_name);
      
      if (!exerciseData.id) {
        toast.error('Please save the exercise before printing');
        return;
      }
      
      // Fetch MSEL injections, coordinators and map objects from API
      let injections = [];
      let coordinators = [];
      let mapObjects = [];
      
      const headers = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      try {
        const mselResponse = await axios.get(`${API}/msel/${exerciseData.id}`, headers);
        injections = mselResponse.data || [];
        console.log('✅ Fetched MSEL injections:', injections.length);
      } catch (error) {
        console.error('❌ Error fetching MSEL:', error);
      }
      
      try {
        const coordResponse = await axios.get(`${API}/team-coordinators/${exerciseData.id}`, headers);
        coordinators = coordResponse.data || [];
        console.log('✅ Fetched coordinators:', coordinators.length);
      } catch (error) {
        console.error('❌ Error fetching coordinators:', error);
      }
      
      try {
        const mapResponse = await axios.get(`${API}/map-objects`, { ...headers, params: { exercise_id: exerciseData.id } });
        mapObjects = mapResponse.data || [];
        console.log('✅ Fetched map objects:', mapObjects.length);
      } catch (error) {
        console.error('❌ Error fetching map objects:', error);
      }
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow pop-ups to print the exercise report');
        return;
      }
      
      // Generate content using shared function
      let printContent = '';
      try {
        printContent = generatePrintContent(injections, coordinators, mapObjects);
      } catch (contentError) {
        console.error('Error generating print content:', contentError);
        printWindow.close();
        toast.error('Error generating report content: ' + contentError.message);
        return;
      }
      
      writeSanitizedHTML(printWindow.document, printContent);
      
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error('Error in print function:', error);
      toast.error('Error generating print report: ' + error.message);
    }
  };

  // Email Report functionality removed

  // Extract print content generation to reusable function
  const generatePrintContent = (injectionsData = [], coordinatorsData = [], mapObjectsData = []) => {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const formatScenarioTime = (v) => {
      if (!v) return 'Not Set';
      try {
        return new Date(v).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
      } catch { return String(v); }
    };
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${exerciseData.exercise_name || 'Exercise Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            h1 { color: #ea580c; text-align: center; border-bottom: 3px solid #ea580c; padding-bottom: 10px; }
            h2 { color: #ea580c; margin-top: 30px; border-bottom: 2px solid #ea580c; padding-bottom: 5px; }
            h3 { color: #666; margin-top: 20px; }
            .meta { color: #555; font-size: 12px; margin: 4px 0 24px 0; text-align: center; }
            .section { margin-bottom: 30px; page-break-inside: avoid; }
            .field { margin: 10px 0; }
            .label { font-weight: bold; color: #555; }
            .value { margin-left: 10px; }
            .list-item { margin: 15px 0; padding: 10px; background: #f9f9f9; border-left: 3px solid #ea580c; }
            .sub-item { margin: 6px 0 6px 16px; padding: 6px 10px; background: #ffffff; border-left: 2px solid #94a3b8; }
            .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; border: 1px solid #ddd; background: #f7f7f7; color: #333; margin-left: 6px; }
            .badge-done { background: #e7f7ec; color: #0f766e; border-color: #bbe6cd; }
            @media print { .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>${esc(exerciseData.exercise_name || 'Exercise Report')}</h1>
          <div class="meta">Complete Exercise Report · Printed ${esc(new Date().toLocaleString([], { hour12: false }))}</div>
          
          <div class="section">
            <h2>Step 1: Exercise Details</h2>
            <div class="field"><span class="label">Exercise Name:</span> ${esc(exerciseData.exercise_name || 'N/A')}</div>
            <div class="field"><span class="label">Exercise Type:</span> ${esc(exerciseData.exercise_type || 'N/A')}</div>
            <div class="field"><span class="label">Description:</span> ${esc(exerciseData.exercise_description || 'N/A')}</div>
            <div class="field"><span class="label">Location:</span> ${esc(exerciseData.location || 'N/A')}</div>
            <div class="field"><span class="label">Latitude:</span> ${esc(exerciseData.latitude ?? 'N/A')} &nbsp; <span class="label">Longitude:</span> ${esc(exerciseData.longitude ?? 'N/A')} &nbsp; <span class="label">Zoom Level:</span> ${esc(exerciseData.zoom_level ?? 'N/A')}</div>
            <div class="field"><span class="label">Start:</span> ${esc(exerciseData.start_date || 'N/A')} ${esc(exerciseData.start_time || '')} &nbsp; <span class="label">End:</span> ${esc(exerciseData.end_date || 'N/A')} ${esc(exerciseData.end_time || '')}</div>
            ${exerciseData.exercise_image ? `<div class="field" style="margin-top: 12px;"><img src="${exerciseData.exercise_image}" alt="Exercise" style="max-width: 480px; max-height: 320px; border-radius: 6px; border: 1px solid #ccc;"/></div>` : ''}
          </div>

          <div class="section">
            <h2>Step 2: Scenario</h2>
            <div class="field"><span class="label">Scenario Name:</span> ${esc(exerciseData.scenario_name || 'N/A')}</div>
            <div class="field"><span class="label">Description:</span> ${esc(exerciseData.scenario_description || 'N/A')}</div>
          </div>

          <div class="section">
            <h2>Step 3: Purpose</h2>
            <div class="field">${esc(exerciseData.purpose_description || 'N/A')}</div>
          </div>

          <div class="section">
            <h2>Step 4: Scope</h2>
            <div class="field"><span class="label">Description:</span> ${esc(exerciseData.scope_description || 'N/A')}</div>
            <div class="field"><span class="label">Hazards:</span> ${esc(exerciseData.scope_hazards || 'N/A')}</div>
            <div class="field"><span class="label">Geographic Area:</span> ${esc(exerciseData.scope_geographic_area || 'N/A')}</div>
            <div class="field"><span class="label">Functions:</span> ${esc(exerciseData.scope_functions || 'N/A')}</div>
            <div class="field"><span class="label">Exercise Type:</span> ${esc(exerciseData.scope_exercise_type || 'N/A')}</div>
          </div>

          <div class="section">
            <h2>Step 5: Goals &amp; Objectives (${(goals || []).length})</h2>
            ${(goals && goals.length > 0) ? goals.map((goal, idx) => {
              const objs = Array.isArray(goal.objectives) ? goal.objectives : [];
              const legacy = (goal.description || '').trim();
              const rows = objs.length > 0
                ? objs.map((o, i) => {
                    const n = (o.name || '').trim();
                    const d = (o.description || '').trim();
                    const nameHtml = n ? `<strong>${esc(n)}</strong>` : '<em style="color:#888;">(unnamed)</em>';
                    const descHtml = d ? `<div style="margin-top:2px;font-size:12px;color:#444;">${esc(d)}</div>` : '';
                    const ach = o.achieved ? ` <em style="color:#888;">(${esc(o.achieved)})</em>` : '';
                    return `<li style="margin-bottom:4px;">${i + 1}. ${nameHtml}${ach}${descHtml}</li>`;
                  }).join('')
                : (legacy ? `<li>${esc(legacy)}</li>` : '');
              return `
                <div class="list-item">
                  <strong>Goal ${idx + 1}:</strong> ${esc(goal.name || 'N/A')}
                  ${goal.broad_statement ? `<br/><span class="label">Broad Statement:</span> ${esc(goal.broad_statement)}` : ''}
                  ${rows ? `<br/><span class="label">Objectives:</span><ul style="margin:4px 0 0 22px; padding:0;">${rows}</ul>` : ''}
                </div>
              `;
            }).join('') : '<p>No goals added</p>'}
          </div>

          <div class="section">
            <h2>Step 6: Functions (${(functions || []).length})</h2>
            ${(functions && functions.length > 0) ? functions.map((func, idx) => `
              <div class="list-item">
                <strong>Function ${idx + 1}:</strong> ${esc(func.name || 'N/A')}<br/>
                <span class="label">Description:</span> ${esc(func.description || 'N/A')}
              </div>
            `).join('') : '<p>No functions added</p>'}
          </div>

          <div class="section">
            <h2>Step 7: Injections / MSEL (${(injectionsData || []).length})</h2>
            ${(injectionsData && injectionsData.length > 0) ? [...injectionsData].sort((a,b)=> (a.event_number||0)-(b.event_number||0)).map((inj) => `
              <div class="list-item">
                <strong>Event #${esc(inj.event_number ?? 'N/A')}</strong>${inj.msel_name ? ` — ${esc(inj.msel_name)}` : ''}
                ${inj.completed ? '<span class="badge badge-done">Completed</span>' : ''}
                <br/>
                <span class="label">Scenario Date/Time:</span> ${esc(formatScenarioTime(inj.scenario_time))}<br/>
                <span class="label">Event Type:</span> ${esc(inj.event_type || 'N/A')} &nbsp; <span class="label">Inject Mode:</span> ${esc(inj.inject_mode || 'N/A')}<br/>
                <span class="label">From → To:</span> ${esc(inj.from_entity || 'N/A')} → ${esc(inj.to_entity || 'N/A')}<br/>
                <span class="label">Message:</span> ${esc(inj.message || 'N/A')}<br/>
                <span class="label">Expected Response:</span> ${esc(inj.expected_response || 'N/A')}<br/>
                <span class="label">Objective/Capability/Task:</span> ${esc(inj.objective_capability_task || 'N/A')}
                ${inj.notes ? `<br/><span class="label">Controller Notes:</span> ${esc(inj.notes)}` : ''}
                ${inj.completed && inj.actual_time ? `<br/><span class="label">Completed At:</span> ${esc(inj.actual_time)}` : ''}
              </div>
            `).join('') : '<p>No injections added</p>'}
          </div>

          <div class="section">
            <h2>Step 8: Organizations (${(organizations || []).length})</h2>
            ${(organizations && organizations.length > 0) ? organizations.map((org, idx) => {
              const persons = Array.isArray(org.persons) ? org.persons : [];
              const personsHtml = persons.length > 0 ? `
                <br/><span class="label">Persons (${persons.length}):</span>
                ${persons.map((p) => `
                  <div class="sub-item">
                    <strong>${esc([p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unnamed')}</strong>
                    ${p.role ? ` — ${esc(p.role)}` : ''}
                    ${p.physical_location ? `<br/><span class="label">Location:</span> ${esc(p.physical_location)}` : ''}
                    ${p.phone || p.email ? `<br/><span class="label">Phone:</span> ${esc(p.phone || 'N/A')} &nbsp; <span class="label">Email:</span> ${esc(p.email || 'N/A')}` : ''}
                  </div>
                `).join('')}
              ` : '';
              return `
                <div class="list-item">
                  <strong>Organization ${idx + 1}:</strong> ${esc(org.name || 'N/A')}<br/>
                  <span class="label">Description:</span> ${esc(org.description || 'N/A')}<br/>
                  <span class="label">Home Base:</span> ${esc(org.home_base || 'N/A')}<br/>
                  <span class="label">Latitude:</span> ${esc(org.latitude ?? 'N/A')} &nbsp; <span class="label">Longitude:</span> ${esc(org.longitude ?? 'N/A')}<br/>
                  <span class="label">Contact:</span> ${esc([org.contact_first_name, org.contact_last_name].filter(Boolean).join(' ') || 'N/A')}<br/>
                  <span class="label">Phone:</span> ${esc(org.contact_phone || 'N/A')} &nbsp; <span class="label">Email:</span> ${esc(org.contact_email || 'N/A')}
                  ${org.organization_image ? `<br/><img src="${org.organization_image}" alt="Org" style="max-width: 240px; margin-top: 6px; border-radius: 4px;"/>` : ''}
                  ${personsHtml}
                </div>
              `;
            }).join('') : '<p>No organizations added</p>'}
          </div>

          <div class="section">
            <h2>Step 9: Exercise Sites (${((exerciseData.exercise_sites) || []).length})</h2>
            ${(exerciseData.exercise_sites && exerciseData.exercise_sites.length > 0) ? exerciseData.exercise_sites.map((site, idx) => `
              <div class="list-item">
                <strong>Site ${idx + 1}:</strong> ${esc(site.proposed_location || 'N/A')}<br/>
                <span class="label">Exercise Function:</span> ${esc(site.exercise_function || 'N/A')}<br/>
                <span class="label">Participant Guidance:</span> ${esc(site.participant_guidance || 'N/A')}
                ${site.image ? `<br/><img src="${site.image}" alt="Site" style="max-width: 320px; margin-top: 6px; border-radius: 4px;"/>` : ''}
              </div>
            `).join('') : '<p>No exercise sites added</p>'}
          </div>

          <div class="section">
            <h2>Step 10: Team Coordinators (${(coordinatorsData || []).length})</h2>
            ${(coordinatorsData && coordinatorsData.length > 0) ? coordinatorsData.map((coord, idx) => `
              <div class="list-item">
                <strong>Coordinator ${idx + 1}:</strong> ${esc(coord.name || 'N/A')}<br/>
                <span class="label">Role:</span> ${esc(coord.role || 'N/A')}<br/>
                <span class="label">Email:</span> ${esc(coord.email || 'N/A')} &nbsp; <span class="label">Phone:</span> ${esc(coord.phone || 'N/A')}<br/>
                <span class="label">Assigned To:</span> ${esc(coord.assignedTo || coord.assigned_to || 'N/A')}
              </div>
            `).join('') : '<p>No coordinators added</p>'}
          </div>

          <div class="section">
            <h2>Step 11: Code Words (${(codeWords || []).length})</h2>
            ${(codeWords && codeWords.length > 0) ? codeWords.map((code, idx) => `
              <div class="list-item">
                <strong>Code Word ${idx + 1}:</strong> ${esc(code.word || 'N/A')}<br/>
                <span class="label">Meaning:</span> ${esc(code.meaning || 'N/A')}
              </div>
            `).join('') : '<p>No code words added</p>'}
          </div>

          <div class="section">
            <h2>Step 12: Callsigns &amp; Frequencies</h2>
            <h3 style="margin-top:8px;">Callsigns (${(callsigns || []).length})</h3>
            ${(callsigns && callsigns.length > 0) ? callsigns.map((call, idx) => `
              <div class="list-item">
                <strong>Callsign ${idx + 1}:</strong> ${esc(call.callsign || 'N/A')}<br/>
                <span class="label">Description:</span> ${esc(call.description || call.assignment || 'N/A')}
              </div>
            `).join('') : '<p>No callsigns added</p>'}
            <h3 style="margin-top:8px;">Frequencies (${(frequencies || []).length})</h3>
            ${(frequencies && frequencies.length > 0) ? frequencies.map((f, idx) => `
              <div class="list-item">
                <strong>Frequency ${idx + 1}:</strong> ${esc(f.frequency || 'N/A')}<br/>
                <span class="label">Type:</span> ${esc(f.type || 'N/A')} &nbsp;
                <span class="label">Modulation:</span> ${esc(f.modulation_type || 'N/A')} &nbsp;
                <span class="label">Primary/Backup:</span> ${esc(f.primary_backup || 'N/A')}<br/>
                <span class="label">Description:</span> ${esc(f.description || 'N/A')}
              </div>
            `).join('') : '<p>No frequencies added</p>'}
          </div>

          <div class="section">
            <h2>Step 13: Assumptions (${(assumptions || []).length})</h2>
            ${(assumptions && assumptions.length > 0) ? assumptions.map((assum, idx) => `
              <div class="list-item">
                <strong>Assumption ${idx + 1}:</strong> ${esc(assum.assumption || 'N/A')}<br/>
                <span class="label">Description:</span> ${esc(assum.description || 'N/A')}
              </div>
            `).join('') : '<p>No assumptions added</p>'}
          </div>

          <div class="section">
            <h2>Step 14: Artificialities (${(artificialities || []).length})</h2>
            ${(artificialities && artificialities.length > 0) ? artificialities.map((art, idx) => `
              <div class="list-item">
                <strong>Artificiality ${idx + 1}:</strong> ${esc(art.artificiality || 'N/A')}<br/>
                <span class="label">Description:</span> ${esc(art.description || 'N/A')}
              </div>
            `).join('') : '<p>No artificialities added</p>'}
          </div>

          <div class="section">
            <h2>Step 15: Safety Concerns (${(safetyConcerns || []).length})</h2>
            ${(safetyConcerns && safetyConcerns.length > 0) ? safetyConcerns.map((safety, idx) => `
              <div class="list-item">
                <strong>Safety Concern ${idx + 1}:</strong> ${esc(safety.concern || 'N/A')}<br/>
                <span class="label">Description:</span> ${esc(safety.description || 'N/A')}
              </div>
            `).join('') : '<p>No safety concerns added</p>'}
          </div>

          <div class="section">
            <h2>Step 16: Standard of Conduct (${(standardsOfConduct || []).length})</h2>
            ${(standardsOfConduct && standardsOfConduct.length > 0) ? standardsOfConduct.map((s, idx) => `
              <div class="list-item">
                <strong>Standard ${idx + 1}:</strong> ${esc(s.name || 'N/A')}<br/>
                <span class="label">Conduct Expected:</span> ${esc(s.conduct_expected || 'N/A')}
              </div>
            `).join('') : '<p>No standards of conduct added</p>'}
          </div>

          <div class="section">
            <h2>Step 17: Mapping (${(mapObjectsData || []).length} object${(mapObjectsData || []).length !== 1 ? 's' : ''})</h2>
            <div class="field"><span class="label">Map Centre Latitude:</span> ${esc(exerciseData.latitude ?? 'N/A')}</div>
            <div class="field"><span class="label">Map Centre Longitude:</span> ${esc(exerciseData.longitude ?? 'N/A')}</div>
            <div class="field"><span class="label">Zoom Level:</span> ${esc(exerciseData.zoom_level ?? 'N/A')}</div>
            <div class="field"><span class="label">Location:</span> ${esc(exerciseData.location || 'N/A')}</div>
            ${(mapObjectsData && mapObjectsData.length > 0) ? `
              <h3 style="margin-top:12px;">Map Objects</h3>
              ${mapObjectsData.map((m, idx) => `
                <div class="list-item">
                  <strong>Object ${idx + 1}:</strong> ${esc(m.name || 'Unnamed')}
                  <span class="badge">${esc(m.object_type || m.type || 'marker')}</span>
                  ${m.locked ? '<span class="badge">Locked</span>' : ''}
                  ${m.description ? `<br/><span class="label">Description:</span> ${esc(m.description)}` : ''}
                  ${m.icon ? `<br/><span class="label">Icon:</span> ${esc(m.icon)}` : ''}
                  ${(m.latitude !== undefined && m.longitude !== undefined) ? `<br/><span class="label">Latitude:</span> ${esc(m.latitude)} &nbsp; <span class="label">Longitude:</span> ${esc(m.longitude)}` : ''}
                  ${m.color ? `<br/><span class="label">Color:</span> ${esc(m.color)}` : ''}
                </div>
              `).join('')}
            ` : '<p style="margin-top:8px; color:#555;">No map objects have been added yet.</p>'}
          </div>
        </body>
      </html>
    `;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Exercise
        return (
          <div className="space-y-6">
            {/* Print and Email Buttons */}
            <div className="flex justify-end gap-3 mb-4">
              <Button
                onClick={handlePrintExercise}
                variant="outline"
                className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Complete Exercise
              </Button>
            </div>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Image</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUploadComponent imageType="exercise_image" title="Exercise" />
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Exercise Name *</Label>
                  <Input
                    value={exerciseData.exercise_name}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, exercise_name: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                    {...getFieldProps('exercise_name')}
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Exercise Type *</Label>
                  <Select 
                    value={exerciseData.exercise_type} 
                    onValueChange={(value) => setExerciseData(prev => ({ ...prev, exercise_type: value }))}
                    disabled={getFieldProps('exercise_type').disabled}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select exercise type" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {exerciseTypes.map((type) => (
                        <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300">Description *</Label>
                  <Textarea
                    value={exerciseData.exercise_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, exercise_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    required
                    {...getFieldProps('exercise_description')}
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Location *</Label>
                  <Input
                    value={exerciseData.location}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, location: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                    {...getFieldProps('eb_location')}
                  />
                </div>

                {/* Latitude, Longitude, and Zoom Level Fields */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <div>
                    <Label className="text-gray-300 block h-10 mb-1">
                      Latitude *
                      <span className="text-xs text-gray-400 ml-1 block">(Decimal: -90 to 90)</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="47.0000"
                      value={exerciseData.latitude}
                      onChange={(e) => handleLatitudeChange(e.target.value, 'exercise')}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== '') {
                          const num = parseFloat(val);
                          if (isNaN(num)) {
                            alert('Latitude must be a valid number (e.g., 47.0000)');
                          } else if (num < -90 || num > 90) {
                            alert('Latitude must be between -90.0000 and 90.0000');
                          } else {
                            const parts = val.split('.');
                            if (parts.length === 1 || (parts[1] && parts[1].length > 4)) {
                              alert('Latitude must have up to 4 decimal places (e.g., 47.0000)');
                            } else {
                              const formatted = num.toFixed(4);
                              handleLatitudeChange(formatted, 'exercise');
                            }
                          }
                        }
                      }}
                      className={`bg-gray-700 border-gray-600 text-white ${
                        exerciseValidationErrors.latitude ? 'border-red-500 border-2' : ''
                      }`}
                      required
                    />
                    {exerciseValidationErrors.latitude && (
                      <p className="text-red-400 text-xs mt-1">{exerciseValidationErrors.latitude}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300 block h-10 mb-1">
                      Longitude *
                      <span className="text-xs text-gray-400 ml-1 block">(Decimal: -180 to 180)</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="-97.0000"
                      value={exerciseData.longitude}
                      onChange={(e) => handleLongitudeChange(e.target.value, 'exercise')}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== '') {
                          const num = parseFloat(val);
                          if (isNaN(num)) {
                            alert('Longitude must be a valid number (e.g., -97.0000)');
                          } else if (num < -180 || num > 180) {
                            alert('Longitude must be between -180.0000 and 180.0000');
                          } else {
                            const parts = val.split('.');
                            if (parts.length === 1 || (parts[1] && parts[1].length > 4)) {
                              alert('Longitude must have up to 4 decimal places (e.g., -97.0000)');
                            } else {
                              const formatted = num.toFixed(4);
                              handleLongitudeChange(formatted, 'exercise');
                            }
                          }
                        }
                      }}
                      className={`bg-gray-700 border-gray-600 text-white ${
                        exerciseValidationErrors.longitude ? 'border-red-500 border-2' : ''
                      }`}
                      required
                    />
                    {exerciseValidationErrors.longitude && (
                      <p className="text-red-400 text-xs mt-1">{exerciseValidationErrors.longitude}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300 block h-10 mb-1">
                      Zoom Level *
                      <span className="text-xs text-gray-400 ml-1 block">(Range: 1-20, supports decimals e.g. 8.5)</span>
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      step="0.1"
                      placeholder="8.0"
                      value={exerciseData.zoom_level || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 20) {
                          setExerciseData(prev => ({ ...prev, zoom_level: value }));
                          setExerciseValidationErrors(prev => ({ ...prev, zoom_level: null }));
                        } else if (e.target.value === '') {
                          setExerciseData(prev => ({ ...prev, zoom_level: '' }));
                        }
                      }}
                      className={`bg-gray-700 border-gray-600 text-white ${
                        exerciseValidationErrors.zoom_level ? 'border-red-500 border-2' : ''
                      }`}
                      required
                    />
                    {exerciseValidationErrors.zoom_level && (
                      <p className="text-red-400 text-xs mt-1">{exerciseValidationErrors.zoom_level}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Start Date *</Label>
                    <Input
                      type="date"
                      value={exerciseData.start_date}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Start Time</Label>
                    <Input
                      type="text" pattern="[0-2][0-9]:[0-5][0-9]" placeholder="HH:MM (24-hour)" maxLength="5"
                      value={exerciseData.start_time}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, start_time: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">End Date *</Label>
                    <Input
                      type="date"
                      value={exerciseData.end_date}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">End Time</Label>
                    <Input
                      type="text" pattern="[0-2][0-9]:[0-5][0-9]" placeholder="HH:MM (24-hour)" maxLength="5"
                      value={exerciseData.end_time}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, end_time: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 3: // Purpose
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Purpose</CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Label className="text-gray-300">Purpose Description *</Label>
                  <Textarea
                    value={exerciseData.purpose_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, purpose_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={4}
                    placeholder="Describe the overall purpose and intent of this exercise..."
                    required
                    {...getFieldProps('purpose_description')}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 4: // Scope
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Scope</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Scope Description *</Label>
                  <Textarea
                    value={exerciseData.scope_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scope_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="Scope defines the boundaries of the exercise, specifically outlining what aspects of an incident and response will be simulated or real and evaluated. It determines the extent and limits of the scenario, the participating entities, and the functions being tested."
                    required
                    {...getFieldProps('scope_description')}
                  />
                </div>

              </CardContent>
            </Card>
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 2: // Scenario
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Scenario Image</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUploadComponent imageType="scenario_image" title="Scenario" />
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Scenario Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Scenario Name *</Label>
                  <Input
                    value={exerciseData.scenario_name}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scenario_name: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                    {...getFieldProps('eb_scenario_name')}
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Scenario Description</Label>
                  <Textarea
                    value={exerciseData.scenario_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scenario_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={4}
                    placeholder="Scenario is an outline or model of a simulated sequence of events that provides the backdrop for the exercise"
                    {...getFieldProps('eb_scenario_description')}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 5: // Goals & Objectives (merged)
        return (
          <div className="space-y-6">
            {/* Goals card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Goals</CardTitle>
                <CardDescription className="text-gray-400">
                  Define the high-level goals for this exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Goal Name *</Label>
                  <Input
                    value={currentGoal.name}
                    onChange={(e) => setCurrentGoal(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Test emergency notification systems"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Broad Statement</Label>
                  <Textarea
                    value={currentGoal.broad_statement || ''}
                    onChange={(e) => setCurrentGoal(prev => ({ ...prev, broad_statement: e.target.value }))}
                    placeholder="...a goal is a broad, overarching statement of what you ultimately want to achieve (like life safety or incident stabilization)"
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <p className="text-xs text-gray-500 italic">
                  Objectives are added under each goal below (once the goal is saved).
                </p>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addGoal}
                    className={editingGoalId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingGoalId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Goal
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Goal
                      </>
                    )}
                  </Button>
                  {editingGoalId && (
                    <Button 
                      onClick={cancelGoalEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button 
                    onClick={saveStepDraft}
                    disabled={loading}
                    variant="outline"
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Step'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Display added goals with nested objectives */}
            {goals.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Goals ({goals.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCollapsedGoals(new Set())}
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="goals-expand-all"
                      >
                        Expand All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCollapsedGoals(new Set(goals.map(g => g.id)))}
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="goals-collapse-all"
                      >
                        Collapse All
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {goals.map((goal) => {
                      const isCollapsed = collapsedGoals.has(goal.id);
                      const objCount = (goal.objectives || []).length;
                      return (
                        <div key={goal.id} className="bg-gray-700 p-4 rounded-lg" data-testid={`goal-card-${goal.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <button
                              type="button"
                              onClick={() => toggleGoalCollapsed(goal.id)}
                              className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors"
                              data-testid={`toggle-goal-${goal.id}`}
                              title={isCollapsed ? 'Expand goal' : 'Collapse to just the title'}
                            >
                              {isCollapsed ? (
                                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              )}
                              <h4 className="text-white font-semibold">{goal.name}</h4>
                              {isCollapsed && (
                                <span className="text-xs text-gray-500 ml-2">
                                  · {objCount} objective{objCount === 1 ? '' : 's'}
                                </span>
                              )}
                            </button>
                            <div className="flex items-center space-x-2 ml-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => editGoal(goal)}
                                className="border-blue-600 text-blue-400 hover:text-blue-300 hover:border-blue-500/50"
                                data-testid={`edit-goal-${goal.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeGoal(goal.id)}
                                className="border-red-600 text-red-400 hover:text-red-300 hover:border-red-500/50"
                                data-testid={`remove-goal-${goal.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            <>
                              {goal.broad_statement ? (
                                <div className="mb-3 rounded-md border border-blue-500/40 bg-blue-500/10 px-3 py-2" data-testid={`goal-broad-statement-${goal.id}`}>
                                  <div className="text-blue-300 text-xs uppercase tracking-wide font-semibold mb-1">Broad Statement</div>
                                  <p className="text-gray-100 text-sm whitespace-pre-wrap">{goal.broad_statement}</p>
                                </div>
                              ) : (
                                <div className="mb-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`goal-broad-statement-empty-${goal.id}`}>
                                  <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Broad Statement</div>
                                  <p className="text-gray-500 text-xs italic">Not provided — click Edit to add a broad statement for this goal.</p>
                                </div>
                              )}
                              {/* Nested objectives */}
                              <NestedObjectives
                                goal={goal}
                                onAdd={(name, description) => addObjectiveToGoal(goal.id, name, description)}
                                onUpdate={(objId, field, value) => updateObjectiveField(goal.id, objId, field, value)}
                                onRemove={(objId) => removeObjectiveFromGoal(goal.id, objId)}
                                onReorder={(from, to) => reorderObjectivesInGoal(goal.id, from, to)}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 6: // Functions
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Functions</CardTitle>
                <CardDescription className="text-gray-400">
                  Define the functions to be exercised and tested
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Function Name *</Label>
                  <Input
                    value={currentFunction.name}
                    onChange={(e) => setCurrentFunction(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Emergency Communications"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Function Description</Label>
                  <Textarea
                    value={currentFunction.description}
                    onChange={(e) => setCurrentFunction(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Function is a set of related tasks and responsibilities that must be performed to manage an incident effectively and provides a standardized, scalable, and modular organizational structure for exercise or emergency response efforts."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addFunction}
                    className={editingFunctionId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingFunctionId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Function
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Function
                      </>
                    )}
                  </Button>
                  {editingFunctionId && (
                    <Button 
                      onClick={cancelFunctionEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Functions Display */}
            {functions.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Functions ({functions.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCollapsedFunctions(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="functions-expand-all">Expand All</Button>
                      <Button variant="outline" size="sm" onClick={() => setCollapsedFunctions(new Set(functions.map(f => f.id)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="functions-collapse-all">Collapse All</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {functions.map((func) => {
                      const isCollapsed = collapsedFunctions.has(func.id);
                      return (
                        <div key={func.id} className="p-4 bg-gray-700 rounded-lg" data-testid={`function-card-${func.id}`}>
                          <div className="flex justify-between items-start">
                            <button type="button" onClick={() => toggleFunctionCollapsed(func.id)} className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0" data-testid={`toggle-function-${func.id}`}>
                              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <h3 className="font-semibold text-white truncate">{func.name}</h3>
                              {isCollapsed && func.description && <span className="text-xs text-gray-500 ml-2 truncate">· {func.description.slice(0, 60)}{func.description.length > 60 ? '…' : ''}</span>}
                            </button>
                            <div className="flex space-x-2 ml-3">
                              <Button onClick={() => editFunction(func)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300"><Edit className="h-4 w-4" /></Button>
                              <Button onClick={() => removeFunction(func.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            func.description ? (
                              <div className="mt-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2" data-testid={`function-description-${func.id}`}>
                                <div className="text-orange-300 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-100 text-sm whitespace-pre-wrap">{func.description}</p>
                              </div>
                            ) : (
                              <div className="mt-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`function-description-empty-${func.id}`}>
                                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-500 text-xs italic">Not provided — click Edit to add a description.</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 7: // Injections (MSEL)
        return (
          <div className="space-y-6">
            <MSELView exerciseId={exerciseData.id} />
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 8: // Organizations
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Organizations</CardTitle>
                <CardDescription className="text-gray-400">
                  Add organizations participating in the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Organization Image Upload */}
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <Label className="text-gray-300 mb-3 block">Organization Image</Label>
                  <div className="flex items-center space-x-6">
                    <div
                      {...orgImageDropProps}
                      onClick={() => document.getElementById('organization-image-upload')?.click()}
                      data-testid="organization-image-dropzone"
                      title="Click to browse or drop an image here"
                      className={`w-24 h-24 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
                        isOrgImageDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
                      }`}
                    >
                      {organizationImagePreview ? (
                        <img src={organizationImagePreview} alt="Organization" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <div className="text-center px-1">
                          <Camera className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                          <span className="text-xs text-gray-400">{isOrgImageDragging ? 'Drop' : 'Drop here'}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleOrganizationImageUpload}
                          className="hidden"
                          id="organization-image-upload"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('organization-image-upload').click()}
                          className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Image
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleOrganizationCameraCapture}
                          className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          Use Camera
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400">
                        Add a logo or photo of the organization
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Organization Name *</Label>
                  <Input
                    value={currentOrganization.name}
                    onChange={(e) => setCurrentOrganization(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., City Emergency Services"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Description</Label>
                  <Textarea
                    value={currentOrganization.description}
                    onChange={(e) => setCurrentOrganization(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the organization's role..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={2}
                  />
                </div>

                <div className="flex space-x-3">
                  <Button 
                    onClick={addOrganization}
                    className={editingOrganizationId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingOrganizationId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Organization
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Organization
                      </>
                    )}
                  </Button>
                  {editingOrganizationId && (
                    <Button 
                      onClick={cancelOrganizationEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Organizations Display */}
            {organizations.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Organizations ({organizations.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setExpandedOrgIds(new Set(organizations.map(o => o.id)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="organizations-expand-all">Expand All</Button>
                      <Button variant="outline" size="sm" onClick={() => setExpandedOrgIds(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="organizations-collapse-all">Collapse All</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {organizations.map((org) => {
                      const expanded = expandedOrgIds.has(org.id);
                      const persons = Array.isArray(org.persons) ? org.persons : [];
                      const personForm = getPersonForm(org.id);
                      const isEditingPerson = editingPersonKey?.startsWith(`${org.id}:`);
                      return (
                      <div key={org.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-3 flex-1">
                            <button
                              type="button"
                              onClick={() => toggleOrgExpanded(org.id)}
                              className="mt-1 text-gray-400 hover:text-orange-400 flex-shrink-0"
                              title={expanded ? 'Collapse persons' : 'Expand persons'}
                              data-testid={`org-toggle-${org.id}`}
                            >
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                            {/* Organization Image */}
                            <div className="w-16 h-16 rounded-lg bg-gray-600 flex-shrink-0 overflow-hidden">
                              {org.organization_image ? (
                                <img src={org.organization_image} alt={org.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Building className="h-8 w-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-white">
                                {org.name}
                                {persons.length > 0 && (
                                  <span className="ml-2 text-xs text-gray-400">({persons.length} {persons.length === 1 ? 'person' : 'persons'})</span>
                                )}
                              </h3>
                              <p className="text-gray-300 text-sm">{org.description}</p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => editOrganization(org)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => removeOrganization(org.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {expanded && (
                          <div className="mt-4 ml-7 border-t border-gray-600 pt-4 space-y-4" data-testid={`org-persons-${org.id}`}>
                            {/* Existing persons list */}
                            {persons.length > 0 && (
                              <div className="space-y-2">
                                {persons.map((p) => (
                                  <div key={p.id} className="p-3 bg-gray-800 rounded flex justify-between items-start">
                                    <div className="flex-1 text-sm">
                                      <div className="text-white font-semibold">
                                        {p.last_name}, {p.first_name}
                                        {p.title && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-orange-500/20 text-orange-300">{p.title}</span>}
                                        {p.facility && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">{p.facility}</span>}
                                      </div>
                                      <div className="text-gray-400 mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                                        {p.physical_location && <span>📍 {p.physical_location}</span>}
                                        {p.phone && <span>📞 {p.phone}{p.phone_extension ? ` ext. ${p.phone_extension}` : ''}</span>}
                                        {p.email && <span className="md:col-span-2">✉ {p.email}</span>}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 ml-2">
                                      <Button onClick={() => editPerson(org.id, p)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 h-7 w-7 p-0">
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                      <Button onClick={() => removePerson(org.id, p.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 h-7 w-7 p-0">
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add / Edit person form */}
                            <div className="p-3 bg-gray-800 rounded-lg space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-orange-400">
                                  {isEditingPerson ? 'Edit Person' : 'Add Person'}
                                </h4>
                                {isEditingPerson && (
                                  <Button
                                    onClick={() => { setEditingPersonKey(null); resetPersonForm(org.id); }}
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-400 hover:text-white text-xs"
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-gray-300 text-xs">Last Name *</Label>
                                  <Input
                                    value={personForm.last_name}
                                    onChange={(e) => setPersonForm(org.id, { last_name: e.target.value })}
                                    className="bg-gray-700 border-gray-600 text-white h-9"
                                    data-testid={`person-lastname-${org.id}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-300 text-xs">First Name *</Label>
                                  <Input
                                    value={personForm.first_name}
                                    onChange={(e) => setPersonForm(org.id, { first_name: e.target.value })}
                                    className="bg-gray-700 border-gray-600 text-white h-9"
                                    data-testid={`person-firstname-${org.id}`}
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <Label className="text-gray-300 text-xs">Physical Location</Label>
                                  <Input
                                    value={personForm.physical_location}
                                    onChange={(e) => setPersonForm(org.id, { physical_location: e.target.value })}
                                    placeholder="Office, station, address…"
                                    className="bg-gray-700 border-gray-600 text-white h-9"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-300 text-xs">Phone</Label>
                                  <Input
                                    value={personForm.phone}
                                    onChange={(e) => setPersonForm(org.id, { phone: e.target.value })}
                                    placeholder="+1 (555) 555-5555"
                                    className="bg-gray-700 border-gray-600 text-white h-9"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-300 text-xs">Extension</Label>
                                  <Input
                                    value={personForm.phone_extension}
                                    onChange={(e) => setPersonForm(org.id, { phone_extension: e.target.value })}
                                    placeholder="ext."
                                    className="bg-gray-700 border-gray-600 text-white h-9"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <Label className="text-gray-300 text-xs">Email</Label>
                                  <Input
                                    type="email"
                                    value={personForm.email}
                                    onChange={(e) => setPersonForm(org.id, { email: e.target.value })}
                                    placeholder="name@example.com"
                                    className="bg-gray-700 border-gray-600 text-white h-9"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-300 text-xs">Title / Profession</Label>
                                  <Select
                                    value={personForm.title || ''}
                                    onValueChange={(v) => setPersonForm(org.id, { title: v })}
                                  >
                                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-9" data-testid={`person-title-${org.id}`}>
                                      <SelectValue placeholder="Select title…" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-700 border-gray-600">
                                      {TITLE_OPTIONS.map((opt) => (
                                        <SelectItem key={opt} value={opt} className="text-white focus:bg-gray-600">{opt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-gray-300 text-xs">Facility</Label>
                                  <Select
                                    value={personForm.facility || ''}
                                    onValueChange={(v) => setPersonForm(org.id, { facility: v })}
                                  >
                                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-9" data-testid={`person-facility-${org.id}`}>
                                      <SelectValue placeholder="Select facility…" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-700 border-gray-600">
                                      {FACILITY_OPTIONS.map((opt) => (
                                        <SelectItem key={opt} value={opt} className="text-white focus:bg-gray-600">{opt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  onClick={() => savePersonToOrg(org.id)}
                                  disabled={!personForm.first_name?.trim() || !personForm.last_name?.trim()}
                                  size="sm"
                                  className="bg-orange-500 hover:bg-orange-600 text-black disabled:opacity-50"
                                  data-testid={`person-save-${org.id}`}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {isEditingPerson ? 'Save Changes' : 'Add Person'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 9: { // Exercise Sites — image + proposed_location + exercise_function + participant_guidance
        const handleSiteImageFile = (file, siteId) => {
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (ev) => {
            const img = ev.target.result;
            if (siteId) {
              setExerciseSites(prev => prev.map(s => s.id === siteId ? { ...s, image: img } : s));
            } else {
              setCurrentSite(prev => ({ ...prev, image: img }));
            }
          };
          reader.readAsDataURL(file);
        };
        const handleSiteCamera = async (siteId) => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            const video = document.createElement('video');
            video.srcObject = stream;
            video.play();
            video.addEventListener('loadedmetadata', () => {
              const canvas = document.createElement('canvas');
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              canvas.getContext('2d').drawImage(video, 0, 0);
              const img = canvas.toDataURL('image/jpeg');
              if (siteId) {
                setExerciseSites(prev => prev.map(s => s.id === siteId ? { ...s, image: img } : s));
              } else {
                setCurrentSite(prev => ({ ...prev, image: img }));
              }
              stream.getTracks().forEach(t => t.stop());
            });
          } catch (err) {
            toast.error('Could not access camera');
          }
        };
        const onDrop = (e, siteId) => {
          e.preventDefault();
          const file = e.dataTransfer?.files?.[0];
          if (file && file.type.startsWith('image/')) handleSiteImageFile(file, siteId);
        };
        const addSite = () => {
          if (!currentSite.proposed_location.trim() && !currentSite.exercise_function.trim() && !currentSite.image) {
            toast.error('Add at least a location, function, or image before saving the site');
            return;
          }
          const newSite = { ...currentSite, id: Date.now() };
          setExerciseSites(prev => [...prev, newSite]);
          setCurrentSite({ proposed_location: '', exercise_function: '', participant_guidance: '', image: null });
        };
        const removeSite = (id) => setExerciseSites(prev => prev.filter(s => s.id !== id));
        const updateSite = (id, field, value) => setExerciseSites(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));

        const SiteImageBox = ({ image, siteId, testIdPrefix }) => (
          <div
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => onDrop(e, siteId)}
            onClick={() => document.getElementById(`${testIdPrefix}-file-${siteId || 'new'}`)?.click()}
            data-testid={`${testIdPrefix}-dropzone-${siteId || 'new'}`}
            className="w-32 h-32 rounded-lg bg-gray-700 border-2 border-dashed border-gray-600 hover:border-orange-500/50 flex items-center justify-center overflow-hidden cursor-pointer transition-colors"
          >
            {image ? (
              <img src={image} alt="Site" className="w-full h-full object-cover rounded-lg" />
            ) : (
              <div className="text-center px-2">
                <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <span className="text-xs text-gray-400">Drop / click to upload</span>
              </div>
            )}
          </div>
        );

        return (
          <div className="space-y-6">
            {/* Add-new-site card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Add Exercise Site</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-6 flex-wrap gap-y-3">
                  <SiteImageBox image={currentSite.image} siteId={null} testIdPrefix="site-new" />
                  <input
                    type="file"
                    accept="image/*"
                    id="site-new-file-new"
                    className="hidden"
                    onChange={(e) => handleSiteImageFile(e.target.files[0], null)}
                  />
                  <div className="space-y-2">
                    <div className="flex gap-3 flex-wrap">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('site-new-file-new').click()}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        data-testid="site-new-upload-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" /> Upload Photo
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleSiteCamera(null)}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        data-testid="site-new-camera-btn"
                      >
                        <Camera className="h-4 w-4 mr-2" /> Take Photo
                      </Button>
                    </div>
                    <p className="text-sm text-gray-400">Upload, capture, or drag &amp; drop onto the image area</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Proposed Location</Label>
                  <Input
                    value={currentSite.proposed_location}
                    onChange={(e) => setCurrentSite(prev => ({ ...prev, proposed_location: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g. Brandon Fire Hall #2 — 100 Main Street"
                    data-testid="site-new-location"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Exercise Function</Label>
                  <Input
                    value={currentSite.exercise_function}
                    onChange={(e) => setCurrentSite(prev => ({ ...prev, exercise_function: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g. Staging area / EOC / Casualty collection point"
                    data-testid="site-new-function"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Participant Guidance</Label>
                  <Textarea
                    value={currentSite.participant_guidance}
                    onChange={(e) => setCurrentSite(prev => ({ ...prev, participant_guidance: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="Directions, parking, sign-in, PPE, safety notes for participants arriving at this site…"
                    data-testid="site-new-guidance"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={addSite}
                    className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                    data-testid="site-add-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Site
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Existing sites list */}
            {exerciseSites.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Sites ({exerciseSites.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCollapsedSites(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="sites-expand-all">Expand All</Button>
                      <Button variant="outline" size="sm" onClick={() => setCollapsedSites(new Set(exerciseSites.map(s => s.id)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="sites-collapse-all">Collapse All</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {exerciseSites.map((site) => {
                      const isCollapsed = collapsedSites.has(site.id);
                      const previewParts = [site.exercise_function, site.participant_guidance].filter(Boolean);
                      const preview = previewParts.join(' · ');
                      return (
                        <div key={site.id} className="p-4 bg-gray-700 rounded-lg" data-testid={`site-card-${site.id}`}>
                          <div className="flex justify-between items-start">
                            <button type="button" onClick={() => toggleSiteCollapsed(site.id)} className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0" data-testid={`toggle-site-${site.id}`}>
                              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <h3 className="font-semibold text-white text-lg truncate">{site.proposed_location || 'Untitled site'}</h3>
                              {isCollapsed && preview && <span className="text-xs text-gray-500 ml-2 truncate">· {preview.slice(0, 60)}{preview.length > 60 ? '…' : ''}</span>}
                            </button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeSite(site.id)}
                              className="ml-3 border-red-500/40 text-red-400 hover:bg-red-500/10"
                              data-testid={`site-remove-${site.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" /> Remove
                            </Button>
                          </div>
                          {!isCollapsed && (
                            <div className="mt-4 space-y-4">
                              <div className="flex items-start space-x-6 flex-wrap gap-y-3">
                                <SiteImageBox image={site.image} siteId={site.id} testIdPrefix="site" />
                                <input
                                  type="file"
                                  accept="image/*"
                                  id={`site-file-${site.id}`}
                                  className="hidden"
                                  onChange={(e) => handleSiteImageFile(e.target.files[0], site.id)}
                                />
                                <div className="flex gap-3 flex-wrap">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => document.getElementById(`site-file-${site.id}`).click()}
                                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                                  >
                                    <Upload className="h-4 w-4 mr-2" /> Upload
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleSiteCamera(site.id)}
                                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                                  >
                                    <Camera className="h-4 w-4 mr-2" /> Camera
                                  </Button>
                                </div>
                              </div>
                              <div>
                                <Label className="text-gray-300">Proposed Location</Label>
                                <Input
                                  value={site.proposed_location || ''}
                                  onChange={(e) => updateSite(site.id, 'proposed_location', e.target.value)}
                                  className="bg-gray-700 border-gray-600 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-300">Exercise Function</Label>
                                <Input
                                  value={site.exercise_function || ''}
                                  onChange={(e) => updateSite(site.id, 'exercise_function', e.target.value)}
                                  className="bg-gray-700 border-gray-600 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-300">Participant Guidance</Label>
                                <Textarea
                                  value={site.participant_guidance || ''}
                                  onChange={(e) => updateSite(site.id, 'participant_guidance', e.target.value)}
                                  className="bg-gray-700 border-gray-600 text-white"
                                  rows={3}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Save Step */}
            <div className="flex justify-end">
              <Button
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                data-testid="site-save-step-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );
      }

      case 10: // Team Coordinators
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Team Coordinators</CardTitle>
                <CardDescription className="text-gray-400">
                  Select participants who will serve as team coordinators
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded">
                  <div className="flex items-center space-x-3">
                    <Users className="h-8 w-8 text-green-400" />
                    <div>
                      <h3 className="text-green-300 font-semibold">Participant Integration</h3>
                      <p className="text-gray-400 text-sm">Import eligible participants with coordinator roles</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    onClick={loadCoordinators}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load Coordinators'}
                  </Button>
                </div>
                <div className="bg-gray-700/50 p-4 rounded">
                  <p className="text-gray-400 text-sm">
                    Coordinators will be automatically selected from participants matching these criteria:
                  </p>
                  <ul className="text-gray-400 text-sm mt-2 space-y-1">
                    <li>• Position equals "Team Coordinator"</li>
                    <li>• Assigned Exercise equals the current exercise</li>
                    <li>• "Participant is involved in the current exercise" checkbox is checked</li>
                  </ul>
                </div>
                
                {/* Display loaded coordinators */}
                {exerciseData?.coordinators?.length > 0 && (
                  <Card className="bg-gray-700/50 border-gray-600">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-green-400 text-lg">Loaded Coordinators ({exerciseData.coordinators.length})</CardTitle>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setCollapsedCoordinators(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="coordinators-expand-all">Expand All</Button>
                          <Button variant="outline" size="sm" onClick={() => setCollapsedCoordinators(new Set(exerciseData.coordinators.map((c, i) => c.id || `coord-${i}`)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="coordinators-collapse-all">Collapse All</Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {exerciseData.coordinators.map((coordinator, index) => {
                          const coordKey = coordinator.id || `coord-${index}`;
                          const isCollapsed = collapsedCoordinators.has(coordKey);
                          const preview = [coordinator.role, coordinator.email].filter(Boolean).join(' · ');
                          return (
                            <div key={coordKey} className="bg-gray-800 p-4 rounded border border-gray-600" data-testid={`coordinator-card-${coordKey}`}>
                              <div className="flex justify-between items-start">
                                <button type="button" onClick={() => toggleCoordinatorCollapsed(coordKey)} className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0" data-testid={`toggle-coordinator-${coordKey}`}>
                                  {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                                  <h4 className="font-semibold text-white truncate">{coordinator.name || 'Unnamed coordinator'}</h4>
                                  {isCollapsed && preview && <span className="text-xs text-gray-500 ml-2 truncate">· {preview.slice(0, 60)}{preview.length > 60 ? '…' : ''}</span>}
                                </button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-3 text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                                  onClick={() => {
                                    const updatedCoordinators = exerciseData.coordinators.filter((_, i) => i !== index);
                                    setExerciseData(prev => ({ ...prev, coordinators: updatedCoordinators }));
                                  }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                              {!isCollapsed && (
                                <div className="mt-3 rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-200">
                                  <div><span className="text-gray-400">Role:</span> {coordinator.role || 'N/A'}</div>
                                  <div><span className="text-gray-400">Email:</span> {coordinator.email || 'N/A'}</div>
                                  <div><span className="text-gray-400">Phone:</span> {coordinator.phone || 'N/A'}</div>
                                  <div><span className="text-gray-400">Assigned to:</span> {coordinator.assignedTo || 'N/A'}</div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 text-sm text-gray-400">
                        Total coordinators: {exerciseData.coordinators.length}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 11: // Code Words
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Code Words</CardTitle>
                <CardDescription className="text-gray-400">
                  Define code words used during the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Code Word *</Label>
                  <Input
                    value={currentCodeWord.word}
                    onChange={(e) => setCurrentCodeWord(prev => ({ ...prev, word: e.target.value }))}
                    placeholder="e.g., THUNDERBOLT"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Definition</Label>
                  <Textarea
                    value={currentCodeWord.meaning}
                    onChange={(e) => setCurrentCodeWord(prev => ({ ...prev, meaning: e.target.value }))}
                    placeholder="Define what this code word means..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addCodeWord}
                    className={editingCodeWordId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingCodeWordId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Code Word
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Code Word
                      </>
                    )}
                  </Button>
                  {editingCodeWordId && (
                    <Button 
                      onClick={cancelCodeWordEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Code Words Display */}
            {codeWords.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Code Words ({codeWords.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCollapsedCodeWords(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="codewords-expand-all">Expand All</Button>
                      <Button variant="outline" size="sm" onClick={() => setCollapsedCodeWords(new Set(codeWords.map(c => c.id)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="codewords-collapse-all">Collapse All</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {codeWords.map((codeWord) => {
                      const isCollapsed = collapsedCodeWords.has(codeWord.id);
                      return (
                        <div key={codeWord.id} className="p-4 bg-gray-700 rounded-lg" data-testid={`codeword-card-${codeWord.id}`}>
                          <div className="flex justify-between items-start">
                            <button type="button" onClick={() => toggleCodeWordCollapsed(codeWord.id)} className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0" data-testid={`toggle-codeword-${codeWord.id}`}>
                              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <h3 className="font-semibold text-white text-lg truncate">{codeWord.word}</h3>
                              {isCollapsed && codeWord.meaning && <span className="text-xs text-gray-500 ml-2 truncate">· {codeWord.meaning.slice(0, 60)}{codeWord.meaning.length > 60 ? '…' : ''}</span>}
                            </button>
                            <div className="flex space-x-2 ml-3">
                              <Button onClick={() => editCodeWord(codeWord)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300"><Edit className="h-4 w-4" /></Button>
                              <Button onClick={() => removeCodeWord(codeWord.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            codeWord.meaning ? (
                              <div className="mt-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2" data-testid={`codeword-meaning-${codeWord.id}`}>
                                <div className="text-orange-300 text-xs uppercase tracking-wide font-semibold mb-1">Meaning</div>
                                <p className="text-gray-100 text-sm whitespace-pre-wrap">{codeWord.meaning}</p>
                              </div>
                            ) : (
                              <div className="mt-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`codeword-meaning-empty-${codeWord.id}`}>
                                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Meaning</div>
                                <p className="text-gray-500 text-xs italic">Not provided — click Edit to add a meaning.</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 12: // Callsigns
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Communication Callsigns</CardTitle>
                <CardDescription className="text-gray-400">
                  Define radio callsigns for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Callsign *</Label>
                  <Input
                    value={currentCallsign.callsign}
                    onChange={(e) => setCurrentCallsign(prev => ({ ...prev, callsign: e.target.value }))}
                    placeholder="e.g., COMMAND-1"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Definition</Label>
                  <Textarea
                    value={currentCallsign.description}
                    onChange={(e) => setCurrentCallsign(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Define the role or position for this callsign..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addCallsign}
                    className={editingCallsignId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingCallsignId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Callsign
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Callsign
                      </>
                    )}
                  </Button>
                  {editingCallsignId && (
                    <Button 
                      onClick={cancelCallsignEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Callsigns Display */}
            {callsigns.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Callsigns ({callsigns.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCollapsedCallsigns(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="callsigns-expand-all">Expand All</Button>
                      <Button variant="outline" size="sm" onClick={() => setCollapsedCallsigns(new Set(callsigns.map(c => c.id)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="callsigns-collapse-all">Collapse All</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {callsigns.map((callsign) => {
                      const isCollapsed = collapsedCallsigns.has(callsign.id);
                      return (
                        <div key={callsign.id} className="p-4 bg-gray-700 rounded-lg" data-testid={`callsign-card-${callsign.id}`}>
                          <div className="flex justify-between items-start">
                            <button type="button" onClick={() => toggleCallsignCollapsed(callsign.id)} className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0" data-testid={`toggle-callsign-${callsign.id}`}>
                              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <h3 className="font-semibold text-white text-lg truncate">{callsign.callsign}</h3>
                              {isCollapsed && callsign.description && <span className="text-xs text-gray-500 ml-2 truncate">· {callsign.description.slice(0, 60)}{callsign.description.length > 60 ? '…' : ''}</span>}
                            </button>
                            <div className="flex space-x-2 ml-3">
                              <Button onClick={() => editCallsign(callsign)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300"><Edit className="h-4 w-4" /></Button>
                              <Button onClick={() => removeCallsign(callsign.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            callsign.description ? (
                              <div className="mt-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2" data-testid={`callsign-description-${callsign.id}`}>
                                <div className="text-orange-300 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-100 text-sm whitespace-pre-wrap">{callsign.description}</p>
                              </div>
                            ) : (
                              <div className="mt-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`callsign-description-empty-${callsign.id}`}>
                                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-500 text-xs italic">Not provided — click Edit to add a description.</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 13: // Assumptions
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Assumptions</CardTitle>
                <CardDescription className="text-gray-400">
                  Define assumptions made for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Assumption Name *</Label>
                  <Input
                    value={currentAssumption.assumption || ''}
                    onChange={(e) => setCurrentAssumption(prev => ({ ...prev, assumption: e.target.value }))}
                    placeholder="e.g., Weather Conditions"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Assumption Description</Label>
                  <Textarea
                    value={currentAssumption.description || ''}
                    onChange={(e) => setCurrentAssumption(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Assumptions are assertions that are accepted as true or certain to happen without proof to allow the scenario and the exercise to proceed as planned. They serve to create a common baseline understanding of the operating environment for all participants and prevent the exercise from getting sidetracked by uncontrollable or external factors."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addAssumption}
                    className={editingAssumptionId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingAssumptionId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Assumption
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Assumption
                      </>
                    )}
                  </Button>
                  {editingAssumptionId && (
                    <Button 
                      onClick={cancelAssumptionEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Assumptions Display */}
            {assumptions.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Assumptions ({assumptions.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCollapsedAssumptions(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="assumptions-expand-all">Expand All</Button>
                      <Button variant="outline" size="sm" onClick={() => setCollapsedAssumptions(new Set(assumptions.map(a => a.id)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="assumptions-collapse-all">Collapse All</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {assumptions.map((assumption) => {
                      const isCollapsed = collapsedAssumptions.has(assumption.id);
                      return (
                        <div key={assumption.id} className="p-4 bg-gray-700 rounded-lg" data-testid={`assumption-card-${assumption.id}`}>
                          <div className="flex justify-between items-start">
                            <button type="button" onClick={() => toggleAssumptionCollapsed(assumption.id)} className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0" data-testid={`toggle-assumption-${assumption.id}`}>
                              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <h3 className="font-semibold text-white text-lg truncate">{assumption.assumption}</h3>
                              {isCollapsed && assumption.description && <span className="text-xs text-gray-500 ml-2 truncate">· {assumption.description.slice(0, 60)}{assumption.description.length > 60 ? '…' : ''}</span>}
                            </button>
                            <div className="flex space-x-2 ml-3">
                              <Button onClick={() => editAssumption(assumption)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300"><Edit className="h-4 w-4" /></Button>
                              <Button onClick={() => removeAssumption(assumption.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            assumption.description ? (
                              <div className="mt-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2" data-testid={`assumption-description-${assumption.id}`}>
                                <div className="text-orange-300 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-100 text-sm whitespace-pre-wrap">{assumption.description}</p>
                              </div>
                            ) : (
                              <div className="mt-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`assumption-description-empty-${assumption.id}`}>
                                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-500 text-xs italic">Not provided — click Edit to add a description.</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 14: // Artificialities
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Artificialities</CardTitle>
                <CardDescription className="text-gray-400">
                  Define artificial constraints or limitations for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Artificiality Name *</Label>
                  <Input
                    value={currentArtificiality.artificiality || ''}
                    onChange={(e) => setCurrentArtificiality(prev => ({ ...prev, artificiality: e.target.value }))}
                    placeholder="e.g., Simulated Resource Limitations"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Artificiality Description</Label>
                  <Textarea
                    value={currentArtificiality.description || ''}
                    onChange={(e) => setCurrentArtificiality(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the artificial constraint or limitation..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addArtificiality}
                    className={editingArtificialityId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingArtificialityId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Artificiality
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Artificiality
                      </>
                    )}
                  </Button>
                  {editingArtificialityId && (
                    <Button 
                      onClick={cancelArtificialityEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Artificialities Display */}
            {artificialities.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Artificialities ({artificialities.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCollapsedArtificialities(new Set())} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="artificialities-expand-all">Expand All</Button>
                      <Button variant="outline" size="sm" onClick={() => setCollapsedArtificialities(new Set(artificialities.map(a => a.id)))} className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50" data-testid="artificialities-collapse-all">Collapse All</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {artificialities.map((artificiality) => {
                      const isCollapsed = collapsedArtificialities.has(artificiality.id);
                      return (
                        <div key={artificiality.id} className="p-4 bg-gray-700 rounded-lg" data-testid={`artificiality-card-${artificiality.id}`}>
                          <div className="flex justify-between items-start">
                            <button type="button" onClick={() => toggleArtificialityCollapsed(artificiality.id)} className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0" data-testid={`toggle-artificiality-${artificiality.id}`}>
                              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <h3 className="font-semibold text-white text-lg truncate">{artificiality.artificiality}</h3>
                              {isCollapsed && artificiality.description && <span className="text-xs text-gray-500 ml-2 truncate">· {artificiality.description.slice(0, 60)}{artificiality.description.length > 60 ? '…' : ''}</span>}
                            </button>
                            <div className="flex space-x-2 ml-3">
                              <Button onClick={() => editArtificiality(artificiality)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300"><Edit className="h-4 w-4" /></Button>
                              <Button onClick={() => removeArtificiality(artificiality.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            artificiality.description ? (
                              <div className="mt-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2" data-testid={`artificiality-description-${artificiality.id}`}>
                                <div className="text-orange-300 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-100 text-sm whitespace-pre-wrap">{artificiality.description}</p>
                              </div>
                            ) : (
                              <div className="mt-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`artificiality-description-empty-${artificiality.id}`}>
                                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-500 text-xs italic">Not provided — click Edit to add a description.</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 15: // Safety
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Safety Image</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUploadComponent imageType="safety_image" title="Safety" />
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Safety Concerns</CardTitle>
                <CardDescription className="text-gray-400">
                  Document safety concerns and considerations for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Safety Concern Name *</Label>
                  <Input
                    value={currentSafetyConcern.concern || ''}
                    onChange={(e) => setSafetyConcern(prev => ({ ...prev, concern: e.target.value }))}
                    placeholder="e.g., Vehicle Operations"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Safety Concern Description</Label>
                  <Textarea
                    value={currentSafetyConcern.description || ''}
                    onChange={(e) => setSafetyConcern(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the safety concern and mitigation measures..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addSafetyConcern}
                    className={editingSafetyConcernId ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
                  >
                    {editingSafetyConcernId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Safety Concern
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Safety Concern
                      </>
                    )}
                  </Button>
                  {editingSafetyConcernId && (
                    <Button 
                      onClick={cancelSafetyConcernEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Safety Concerns Display */}
            {safetyConcerns.length > 0 && (
              <Card className="bg-gray-800 border-red-500/20">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-red-500">Added Safety Concerns ({safetyConcerns.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCollapsedSafety(new Set())}
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="safety-expand-all"
                      >
                        Expand All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCollapsedSafety(new Set(safetyConcerns.map(s => s.id)))}
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="safety-collapse-all"
                      >
                        Collapse All
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {safetyConcerns.map((concern) => {
                      const isCollapsed = collapsedSafety.has(concern.id);
                      return (
                        <div key={concern.id} className="p-4 bg-gray-700 rounded-lg border-l-4 border-red-500" data-testid={`safety-card-${concern.id}`}>
                          <div className="flex justify-between items-start">
                            <button
                              type="button"
                              onClick={() => toggleSafetyCollapsed(concern.id)}
                              className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors"
                              data-testid={`toggle-safety-${concern.id}`}
                              title={isCollapsed ? 'Expand safety concern' : 'Collapse to just the title'}
                            >
                              {isCollapsed ? (
                                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              )}
                              <ShieldAlert className="h-5 w-5 text-red-400 flex-shrink-0" />
                              <h3 className="font-semibold text-white text-lg">{concern.concern}</h3>
                              {isCollapsed && concern.description && (
                                <span className="text-xs text-gray-500 ml-2 truncate">· {concern.description.slice(0, 60)}{concern.description.length > 60 ? '…' : ''}</span>
                              )}
                            </button>
                            <div className="flex space-x-2 ml-3">
                              <Button
                                onClick={() => editSafetyConcern(concern)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-400 hover:text-blue-300"
                                data-testid={`edit-safety-${concern.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => removeSafetyConcern(concern.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                data-testid={`remove-safety-${concern.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            concern.description ? (
                              <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2" data-testid={`safety-description-${concern.id}`}>
                                <div className="text-red-300 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-100 text-sm whitespace-pre-wrap">{concern.description}</p>
                              </div>
                            ) : (
                              <div className="mt-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`safety-description-empty-${concern.id}`}>
                                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Description</div>
                                <p className="text-gray-500 text-xs italic">Not provided — click Edit to add a description for this safety concern.</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );

      case 16: { // Standard of Conduct
        const addStandard = () => {
          if (!currentStandardOfConduct.name.trim()) return;
          if (editingStandardOfConductId) {
            setStandardsOfConduct(prev => prev.map(s => s.id === editingStandardOfConductId ? { ...currentStandardOfConduct, id: editingStandardOfConductId } : s));
            setEditingStandardOfConductId(null);
          } else {
            setStandardsOfConduct(prev => [...prev, { ...currentStandardOfConduct, id: Date.now() }]);
          }
          setCurrentStandardOfConduct({ name: '', conduct_expected: '' });
        };
        const editStandard = (s) => {
          setCurrentStandardOfConduct({ name: s.name || '', conduct_expected: s.conduct_expected || '' });
          setEditingStandardOfConductId(s.id);
        };
        const removeStandard = (id) => setStandardsOfConduct(prev => prev.filter(s => s.id !== id));

        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Standard of Conduct</CardTitle>
                <CardDescription className="text-gray-400">
                  Document the standards and conduct expected of exercise participants
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Standard Name *</Label>
                  <Input
                    value={currentStandardOfConduct.name}
                    onChange={(e) => setCurrentStandardOfConduct(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Professional Conduct, Confidentiality, Safety Compliance"
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="standard-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Conduct Expected</Label>
                  <Textarea
                    value={currentStandardOfConduct.conduct_expected}
                    onChange={(e) => setCurrentStandardOfConduct(prev => ({ ...prev, conduct_expected: e.target.value }))}
                    placeholder="Describe the conduct expected from participants for this standard..."
                    rows={4}
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="standard-conduct-textarea"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button
                    onClick={addStandard}
                    className={editingStandardOfConductId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="standard-add-btn"
                  >
                    {editingStandardOfConductId ? (
                      <><Save className="h-4 w-4 mr-2" /> Update Standard</>
                    ) : (
                      <><Plus className="h-4 w-4 mr-2" /> Add Standard</>
                    )}
                  </Button>
                  {editingStandardOfConductId && (
                    <Button
                      onClick={() => { setCurrentStandardOfConduct({ name: '', conduct_expected: '' }); setEditingStandardOfConductId(null); }}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={saveStepDraft}
                    disabled={loading}
                    variant="outline"
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Step'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {standardsOfConduct.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-orange-500">Added Standards ({standardsOfConduct.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCollapsedStandards(new Set())}
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="standards-expand-all"
                      >
                        Expand All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCollapsedStandards(new Set(standardsOfConduct.map(s => s.id)))}
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="standards-collapse-all"
                      >
                        Collapse All
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {standardsOfConduct.map((s) => {
                      const isCollapsed = collapsedStandards.has(s.id);
                      return (
                        <div key={s.id} className="bg-gray-700 p-4 rounded-lg border-l-4 border-orange-500" data-testid={`standard-row-${s.id}`}>
                          <div className="flex justify-between items-start gap-3">
                            <button
                              type="button"
                              onClick={() => toggleStandardCollapsed(s.id)}
                              className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0"
                              data-testid={`toggle-standard-${s.id}`}
                              title={isCollapsed ? 'Expand standard' : 'Collapse to just the title'}
                            >
                              {isCollapsed ? (
                                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              )}
                              <h4 className="text-white font-semibold truncate">{s.name}</h4>
                              {isCollapsed && s.conduct_expected && (
                                <span className="text-xs text-gray-500 ml-2 truncate">· {s.conduct_expected.slice(0, 60)}{s.conduct_expected.length > 60 ? '…' : ''}</span>
                              )}
                            </button>
                            <div className="flex space-x-2 flex-shrink-0">
                              <Button onClick={() => editStandard(s)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300" data-testid={`edit-standard-${s.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button onClick={() => removeStandard(s.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300" data-testid={`remove-standard-${s.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {!isCollapsed && (
                            s.conduct_expected ? (
                              <div className="mt-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2" data-testid={`standard-conduct-${s.id}`}>
                                <div className="text-orange-300 text-xs uppercase tracking-wide font-semibold mb-1">Conduct Expected</div>
                                <p className="text-gray-100 text-sm whitespace-pre-wrap">{s.conduct_expected}</p>
                              </div>
                            ) : (
                              <div className="mt-3 rounded-md border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2" data-testid={`standard-conduct-empty-${s.id}`}>
                                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Conduct Expected</div>
                                <p className="text-gray-500 text-xs italic">Not provided — click Edit to add the conduct expected for this standard.</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );
      }

      case 17: // Mapping
        return (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <Map className="h-6 w-6 text-blue-400" />
                <div>
                  <h3 className="text-blue-300 font-semibold">Exercise Mapping</h3>
                  <p className="text-gray-400 text-sm">Create and manage map objects for your exercise. Add markers, zones, and routes that will be used during the exercise.</p>
                </div>
              </div>
            </div>

            {/* Map Container with Fixed Height */}
            {exerciseData.id ? (
              <div className="border border-gray-600 rounded-lg overflow-hidden" style={{ height: '700px' }}>
                <LeafletMapping exerciseId={exerciseData.id} />
              </div>
            ) : (
              <div className="bg-yellow-900/20 border border-yellow-500/30 p-6 rounded-lg">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="h-8 w-8 text-yellow-400" />
                  <div>
                    <h3 className="text-yellow-300 font-semibold mb-1">Exercise Not Saved</h3>
                    <p className="text-gray-300 text-sm">Please save the exercise first to enable mapping functionality. Go back to Step 1 and save your exercise details.</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Save Step Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveStepDraft}
                disabled={loading}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Step'}
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="flex">
        {/* Sidebar Progress */}
        <div className="w-80 bg-gray-900 border-r border-orange-500/20 h-screen sticky top-0">
          <div className="p-4">
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="text-orange-500 hover:text-orange-400 mb-6 w-full justify-start"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {returnTo === 'excon-dashboard' ? 'Return to ExCon Dashboard' : 'Back to Exercises'}
            </Button>
            
            <h2 className="text-orange-500 font-bold text-lg mb-2">
              {isEditing ? 'Edit Exercise' : 'Exercise Builder'}
            </h2>

            {/* Auto-save indicator */}
            {exerciseData?.id && (
              <div className="mb-4 text-xs text-gray-400 flex items-center gap-1.5" data-testid="wizard-autosave-indicator">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${autoSaving ? 'bg-orange-400 animate-pulse' : 'bg-green-500'}`} />
                {autoSaving
                  ? 'Auto-saving…'
                  : lastAutoSaved
                    ? `Auto-saved at ${lastAutoSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    : 'Auto-save on'}
              </div>
            )}
            
            <ScrollArea className="h-[calc(100vh-200px)]">
              <div className="space-y-2">
                {steps.map((step) => (
                  <div
                    key={step.number}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      currentStep === step.number
                        ? 'bg-orange-500/20 text-orange-500 border border-orange-500/30'
                        : currentStep > step.number
                        ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                        : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                    }`}
                    onClick={() => setCurrentStep(step.number)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        currentStep === step.number
                          ? 'bg-orange-500 text-black'
                          : currentStep > step.number
                          ? 'bg-green-500 text-black'
                          : 'bg-gray-700 text-gray-400'
                      }`}>
                        {currentStep > step.number ? '✓' : step.number}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-white">{step.title}</div>
                        <div className="text-xs opacity-75">{step.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="p-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-orange-500 mb-2">
                Step {currentStep}: {steps[currentStep - 1]?.title}
              </h1>
              <p className="text-gray-400">{steps[currentStep - 1]?.description}</p>
            </div>

            {returnTo === 'excon-dashboard' && (
              <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-center justify-between">
                <span className="text-orange-400 text-sm">
                  Adding injection for this exercise. When done, click the button to return.
                </span>
                <Button 
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                  onClick={onBack}
                  data-testid="return-to-excon-btn"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Return to ExCon Dashboard
                </Button>
              </div>
            )}

            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-700">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="border-gray-600 text-gray-300"
              >
                Previous
              </Button>

              <div className="text-center">
                <span className="text-gray-400 text-sm">
                  Step {currentStep} of {steps.length}
                </span>
              </div>

              <div className="space-x-3">
                {currentStep === steps.length ? (
                  <Button
                    onClick={saveExercise}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? 'Saving...' : 'Complete Exercise'}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => saveExercise({ stayOnStep: true })}
                      disabled={loading}
                      data-testid="wizard-save-draft-btn"
                      className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                    >
                      Save Draft
                    </Button>
                    <Button
                      onClick={nextStep}
                      className="bg-orange-500 hover:bg-orange-600 text-black"
                    >
                      Next
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default ExerciseBuilderWizard;
